import {
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  HostListener,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isAfter, isBefore, isValid, subDays } from 'date-fns';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

import { SampleActivity, CreateSampleDropRequestBody } from '../../../classes/activity/sample.activity.class';
import { CUSTOMER_LICENSE_TYPE } from '../../../classes/sampling/customer-license.class';
import { AppointmentActivity } from '../../../classes/activity/appointment.activity.class';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '../../../classes/authentication/user.class';
import { IndFormFieldViewDataModel, FormFieldType } from '../../../models/indFormFieldDataModel';
import { Contact, ContactAddress } from '../../../classes/contact/contact.class';
import {
  CustomerSampleAllocation,
} from '../../../classes/sample/customer-sample-allocation.class';
import { Lot } from '../../../classes/sample/lot.class';
import { CaptureAllocationOrderComponent } from '../../capture-allocation-order/capture-allocation-order';
import { SampleDataService } from '../../../data-services/sample/sample.data.service';
import { ActivityService } from '../../../services/activity/activity.service';
import { AlertService } from '../../../services/alert/alert.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { ContactOfflineService } from '../../../services/contact/contact.service';
import { DateTimeFormatsService } from '../../../services/date-time-formats/date-time-formats.service';
import { DeviceService } from '../../../services/device/device.service';
import { NotificationService, ToastStyle } from '../../../services/notification/notification.service';
import { SampleService, SamplingDetailsViewMode } from '../../../services/sample/sample.service';
import { NavigationService, PageName } from '../../../services/navigation/navigation.service';
import { ComponentViewMode } from '../../../services/ui/ui.service';
import { DatePipe } from '@angular/common';
import { Utility } from '@omni/utility/util';
import { ConsentService } from '@omni/services/consent/consent.service';
import { ConsentTerm } from '@omni/classes/consent/consent-term.class';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SampleDetailInSampleActivity, LotDetailInSampleActivity } from '../../../models/sample-model';
import { Section } from '../../shared/ind-form-list/ind-form-list.component';
import { CaseManagementService } from '@omni/services/case-management/case-management.service';
import { Brand } from '@omni/classes/brand/brand.class';
import { BrandOfflineService } from '@omni/services/brand/brand.service';
import { Events } from '@omni/events';
import { AllocationInventoryDataService } from '@omni/data-services/sample/allocation-inventory.data.service';
import { AllocationInventoryService } from '@omni/services/sample/allocation-inventory.service';
import { SampleReconciliationService } from '@omni/services/sample/sample-reconciliation.service';
import { MeetingMandatoryActivitiesConfigService } from '@omni/services/meeting/meeting-mandatory-activities-config.service';

export interface InlineProductRow {
  allocId: string | undefined;
  skuId: string;
  skuName: string;
  lots: InlineLotRow[];
  orderLimit: number | null;
  isUnlimited: boolean;
  remaining: number;
  errorMessage?: string;
  // Enhanced fields from new-sample-activity
  currencySymbol: string;
  totalsamplevaluelimit: number;
  indskr_totalsamplevaluedelivered: number;
  allowvaluelimitunblock: boolean;
  valueRemaining: string;
  pricePerUnit: string;
  customerDropLimit: string;
  onHandQuantity: number;
  eSignatureRequired: boolean;
  isValueLimitExceeded: boolean;
  isQuantityInvalid: boolean;
  isPending?: boolean;
  isRemoving?: boolean;
  isControlled?: boolean;
}

export interface InlineLotRow {
  lotId: string;
  lotName: string;
  expiryDisplay: string;
  lastDispatchDisplay: string;
  quantity: number;
  unitPrice: number;
  totalQuantityRemaining: number;
  lotRefData: Lot | null;
  activityLotRefData: LotDetailInSampleActivity | null;
  isControlled?: boolean;
}

interface TDDDLicenseResult {
  hasCat2: boolean;
  hasCat3: boolean;
}
const EMPTY_TDDD: TDDDLicenseResult = { hasCat2: false, hasCat3: false };

@Component({
  selector: 'inline-allocation-order',
  templateUrl: 'inline-allocation-order.html',
  styleUrls: ['inline-allocation-order.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineAllocationOrderComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('termsBodyEl') termsBodyEl: ElementRef<HTMLDivElement>;
  @ViewChild('productSearchWrap', { static: false }) productSearchWrapEl: ElementRef<HTMLDivElement>;
  @ViewChild('addressSearchWrap', { static: false }) addressSearchWrapEl: ElementRef<HTMLDivElement>;

  @Input() allocation: SampleActivity;
  @Input() isReadOnly = false;
  @Input() isScrapping = false;
  @Input() meetingId: string;
  @Input() meetingContacts: Contact[] = [];
  // Computed: prefer the allocation's own type flag (set from Dynamics) over the
  // @Input so that records loaded from PouchDB or API without indskr_allocationordertype
  // in the POST response still render correctly once the allocation data is available.
  @Input() set isPromoMaterial(val: boolean) { this._isPromoMaterialInput = val; }
  get isPromoMaterial(): boolean { return this._isPromoMaterialInput || this.allocation?.isPromoMaterial || false; }
  private _isPromoMaterialInput = false;

  @Output() onSaved = new EventEmitter<SampleActivity>();
  @Output() onScrapped = new EventEmitter<SampleActivity>();
  @Output() onCompleted = new EventEmitter<SampleActivity>();
  @Output() onRequestSelectContact = new EventEmitter<void>();
  @Output() onContactSelected = new EventEmitter<{ allocation: SampleActivity; contact: Contact; address?: ContactAddress }>();

  // Contact section
  isSearchingContact = false;
  contactSearchText = '';
  contactSearchResults: Contact[] = [];
  nearMeContacts: Contact[] = [];
  nearMePillsLoader = async (): Promise<{ id: string; label: string; onClick: () => void }[]> => {
    const meeting = this.activityService.selectedActivity as AppointmentActivity;
    const excludeIds = new Set<string>();
    if (this.allocation?.contactID) {
      excludeIds.add(this.allocation.contactID);
    }
    this.nearMeContacts = await this.contactService.loadNearMeContacts(meeting, excludeIds);
    if (this.nearMeContacts.length) {
      const ids = new Set(this.nearMeContacts.map(c => c.ID));
      this.contactSearchResults = [...this.nearMeContacts, ...this.meetingContacts.filter(c => !ids.has(c.ID))];
    }
    return this.nearMeContacts.map(c => ({
      id: c.ID,
      label: this._contactDisplayName(c),
      onClick: () => this.selectContact(c),
    }));
  };
  sharedLocationPillsLoader = async (): Promise<{ id: string; label: string; onClick: () => void }[]> => {
    const currentContactId = this.allocation?.contactID;
    const meetingContactIds = (this.meetingContacts || []).map(c => c.ID);
    const sourceContactIds = currentContactId ? [currentContactId] : meetingContactIds;
    if (!sourceContactIds.length) return [];
    const excludeIds = new Set([...this.nearMeContacts.map(c => c.ID), ...(currentContactId ? [currentContactId] : [])]);
    const contacts = await this.contactService.loadSharedLocationContacts(sourceContactIds, excludeIds, this.selectedAddressObj);
    return contacts.map(c => ({
      id: c.ID,
      label: this._contactDisplayName(c),
      onClick: () => this.selectContact(c),
    }));
  };
  contactSourceData: any[] = [];

  // Address section
  contactAddressList: ContactAddress[] = [];
  selectedAddressObj: ContactAddress = null;
  selectedAddress = '';
  isChangingAddress = false;
  addressSearchText = '';
  addressLicenseValid: boolean | null = null;
  addressStateLicenseValid: boolean | null = null;
  addressErrorMessage = '';
  addressLicenseChecking = false;
  addressTDDD: TDDDLicenseResult | null = null;
  isOhioTDDDFlow = false;

  // Product grid
  isInitLoading = true;
  eligibleAllocations: CustomerSampleAllocation[] = [];
  productRows: InlineProductRow[] = [];
  productSearchText = '';
  productSearchResults: CustomerSampleAllocation[] = [];
  isSearchingProduct = false;
  hasReceiptsTab = false;

  // OMNI-62549: lot ids (lowercased) from the rep's last physical count eligible for below-zero
  // sampling. Populated only when RECIEPTS_TAB is on and the device is online.
  private lastPhysicalCountEligibleLotIds: Set<string> = new Set<string>();

  // Terms & Signature
  termsExpanded = false;
  termsViewLess = true;
  termsOverflows = false;
  termsBody: SafeHtml = null;
  rawTermsBody = '';
  consentTerm: ConsentTerm = null;
  isTermsAccepted = false;
  capturedSignature = '';

  // State
  isCompleted = false;
  isScrapped = false;
  isSaving = false;
  contactFormField: IndFormFieldViewDataModel;
  private saveSubject$ = new Subject<void>();
  private ngDestroy$ = new Subject<void>();
  private _isDestroyed = false;
  private _persistInFlight: Promise<void> | null = null;
  /** True when a debounced save is queued but hasn't run yet — flushed on destroy. */
  private _pendingSave = false;
  private hasAllocationOrdersUnrestricted = false;
  private sampleLastDispatchLimitNum = 0;
  private _productDropdownEl: HTMLElement = null;
  private _productDropdownIsMobile = false;
  private _productDropdownSearchInputEl: HTMLInputElement | null = null;
  private _productDropdownResizeHandler: (() => void) | null = null;
  private _productDropdownOutsideClickHandler: ((e: Event) => void) | null = null;
  private _addressDropdownEl: HTMLElement = null;
  private _addressDropdownView: EmbeddedViewRef<void> | null = null;
  @ViewChild('addressDropdownTpl', { static: false }) addressDropdownTpl: TemplateRef<void>;
  private _parentIonContent: HTMLIonContentElement = null;
  private _skuSortOrderMap: Map<string, number> | null = null;
  private _licenseCheckAbort: AbortController | null = null;
  private _maxOrderQtyMap: Map<string, number> = new Map();
  /** Per stateId license result for address dropdown pills (contactId::stateId). */
  private _addressStateLicenseCache = new Map<string, boolean>();
  /** Per addressId TDDD result. TDDD records carry no customer / no state
   *  link (matched purely by address), so addressId alone is the cache key
   *  and entries can survive contact changes. */
  private _addressTDDDCache = new Map<string, TDDDLicenseResult>();
  private _addressLicensePrefetchGen = 0;
  private _tdddCheckEnabled = false;

  get hideSystemOnHandQuantity(): boolean {
    return !!this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_CUSTOMERALLOCATIONS_GA_HIDESYSTEMONHANDQUANTITY);
  }

  constructor(
    private readonly sampleService: SampleService,
    private readonly sampleDataService: SampleDataService,
    private readonly activityService: ActivityService,
    private readonly contactService: ContactOfflineService,
    private readonly authService: AuthenticationService,
    private readonly device: DeviceService,
    private readonly alertService: AlertService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly datePipe: DatePipe,
    private readonly consentService: ConsentService,
    private readonly sanitizer: DomSanitizer,
    private readonly events: Events,
    private readonly cd: ChangeDetectorRef,
    private readonly caseManagementService: CaseManagementService,
    private readonly allocationInventoryDataService: AllocationInventoryDataService,
    private readonly allocationInventoryService: AllocationInventoryService,
    private readonly meetingMandatoryConfig: MeetingMandatoryActivitiesConfigService,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly elRef: ElementRef,
    private readonly navService: NavigationService,
    private readonly appRef: ApplicationRef,
    private readonly brandService: BrandOfflineService,
    private readonly sampleReconciliationService: SampleReconciliationService,
  ) {}

  dismissAddressDropdown() {
    this._destroyAddressDropdown();
    this.isChangingAddress = false;
    this.addressSearchText = '';
    this.cd.markForCheck();
  }

  async ngOnInit() {
    this.hasAllocationOrdersUnrestricted = this.authService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDERS_UNRESTRICTED);
    this.hasReceiptsTab = this.authService.hasFeatureAction(FeatureActionsMap.RECIEPTS_TAB);
    this.sampleLastDispatchLimitNum = this.authService.user.sampleLastDispatchLimit || 0;
    this._tdddCheckEnabled = this.sampleDataService.isTDDDLicenseCheckEnabled();

    // OMNI-62549: load the below-zero-eligible lot set (from the last physical count) before the
    // eligible allocations/lots are built, so below-zero lots surface on the drop screen (AC-1/AC-8).
    await this.loadLastPhysicalCountEligibleLots();

    this._initState();
    this._loadConsentTerms();
    this._setupDebouncedSave();

    // contact-list (CREATE_FROM_MEETING path) publishes 'updateEmbeddedActivity' with the allocation
    // after setting contactID/contactName on inMeetingAllocationActivity — a genuine contact
    // (re)selection on a DRAFT order, which should reload contact info and reset the address.
    //
    // IMPORTANT: updateSampleActivity ALSO publishes 'updateEmbeddedActivity' — but only on
    // COMPLETION (state === 1). Reacting to that would run _onContactSelectedFromPage, which
    // clears the address + products of the order that was JUST completed and then re-picks/
    // re-persists (offline, with no loaded addresses, an EMPTY address) — wiping the customer
    // address on completed in-meeting allocation orders. Only handle the contact-selection case:
    // ignore completion echoes (state === 1) and never reset an already finalized order.
    this.events.subscribe('updateEmbeddedActivity', async (updatedActivity: SampleActivity) => {
      if (updatedActivity?.ID === this.allocation?.ID
          && updatedActivity?.state !== 1
          && !this.isCompleted
          && !this.isScrapped) {
        await this._onContactSelectedFromPage();
      }
    });

    // Render shell immediately — no loading gate
    this.isInitLoading = false;
    this.cd.markForCheck();

    if (this.allocation?.contactID) {
      await this._loadContactInfo(this.allocation.contactID);
      if (this.isLocked) {
        this.addressLicenseValid = true;
        this.addressStateLicenseValid = true;
        await this._resolveLockedSelectedAddress();
        await this._resolveLockedLicenseBadges();
      } else {
        await this._loadAddresses(this.allocation.contactID);
        if (this.allocation?.addressID) {
          this._applyExistingAddress();
        } else {
          await this._autoSelectPrimaryAddress();
        }
      }
    } else if (this.allocation?.location) {
      this.selectedAddress = this.allocation.location;
    }
    if (!this.isLocked) {
      await this._ensureAllocationsLoaded();
      this._loadEligibleAllocations();
    }
    this._buildProductRows();
    this.cd.detectChanges();
    // Validate license for pre-existing address (non-blocking, after products are built)
    if (!this.isLocked && this.selectedAddressObj && this.addressLicenseValid === null) {
      this._validateLicenseInBackground(this.selectedAddressObj);
    }
    this._onlineSyncOnInit();
  }

  private async _onContactSelectedFromPage() {
    if (!this.allocation?.contactID) return;
    if (this.isLocked) return;
    this.isSearchingContact = false;
    this._removeCustomerAllocationProducts();
    this.eligibleAllocations = [];
    this.isTermsAccepted = false;
    this._applyAddress(null);
    this.addressLicenseChecking = true;

    this._loadContactInfo(this.allocation.contactID);
    this._buildContactFormField();
    await this._loadAddresses(this.allocation.contactID);
    await this._ensureAllocationsLoaded();
    this.cd.detectChanges();
    await this.sampleDataService.syncCustomerLicensesForContact(this.allocation.contactID);

    if (!this.selectedAddressObj) {
      this._autoSelectAddressAndRefresh();
    } else {
      this.addressLicenseChecking = false;
      this.cd.detectChanges();
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['allocation'] && !changes['allocation'].firstChange) {
        this._initState();
        if (this.allocation?.contactID) {
          await this._loadContactInfo(this.allocation.contactID);
          if (this.isLocked) {
            this.addressLicenseValid = true;
            this.addressStateLicenseValid = true;
            await this._resolveLockedSelectedAddress();
            await this._resolveLockedLicenseBadges();
            this._buildProductRows();
          } else {
            await this._loadAddresses(this.allocation.contactID);
            if (this.allocation?.addressID) {
              this._applyExistingAddress();
            } else {
              await this._autoSelectPrimaryAddress();
            }
            await this._ensureAllocationsLoaded();
            this._loadEligibleAllocations();
            this._buildProductRows();
            if (this.selectedAddressObj && this.addressLicenseValid === null) {
              this._validateLicenseInBackground(this.selectedAddressObj);
            }
          }
      }
      this._onlineSyncOnInit();
    }
    // When meeting contacts are updated (e.g. after contact selection from Contact Page),
    // auto-select the first new contact if allocation has no contact yet
    if (changes['meetingContacts'] && !changes['meetingContacts'].firstChange) {
      if (!this.allocation?.contactID && this.meetingContacts?.length) {
        await this.selectContact(this.meetingContacts[0]);
      }
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    // If a text input within this component is focused, the resize is likely from
    // the Electron virtual keyboard — skip to keep focus, keyboard, and dropdowns open
    const activeEl = document.activeElement as HTMLElement;
    if (activeEl && this.elRef.nativeElement.contains(activeEl)) {
      const tag = activeEl.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      activeEl.blur();
    }
    
    if (this._productDropdownIsMobile) return;
    this._destroyProductDropdown();
    this._destroyAddressDropdown();
    this.isSearchingProduct = false;
    this.isChangingAddress = false;
    this.productSearchText = '';
    this.addressSearchText = '';
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    this._isDestroyed = true;
    this._destroyProductDropdown();
    this._destroyAddressDropdown();
    this._destroyQuantityDropdown();
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  // ─── State Init ───────────────────────────────────────────────────────────

  private _initState() {
    this.isCompleted = this.allocation?.state === 1 || this.allocation?.statusString === 'Completed';
    this.isScrapped = this.allocation?.state === 2;
    this.capturedSignature = this.allocation?.signature || '';
    if (this.isCompleted) {
      this.isTermsAccepted = true;
    }
    this.isSearchingContact = !this.allocation?.contactID;
    this.isChangingAddress = false;
    this._buildContactFormField();
  }

  private get isLocked(): boolean {
    return this.isCompleted || this.isReadOnly || this.isScrapped || this.isSaving || this.isScrapping;
  }

  private _buildContactFormField() {
    const isEditable = !this.isLocked;
    const hasContact = !!this.allocation?.contactID;

    const endIcons = isEditable ? [
      { iconName: 'searchblue', overRideDisabledProperty: false },
      { iconName: 'expandblue', overRideDisabledProperty: true },
    ] : [];

    this._buildContactSourceData();
    const selectedChipData = this._getSelectedContactData();
    const meetingContactData = this._getMeetingContactData();

    let selectedData: any[];
    if (hasContact) {
      selectedData = selectedChipData;
    } else if (meetingContactData.length > 0) {
      selectedData = meetingContactData;
    } else {
      selectedData = [{ id: '_iao_gate', title: '', image: '', refObject: null }];
    }
    const hideInlineChip = !hasContact;

    const sections = this._generateContactSections(hasContact ? selectedChipData : [], meetingContactData);

    this.contactFormField = {
      label: this.translate.instant('CUSTOMER'),
      inputText: '',
      customPlaceholderLabel: hasContact
        ? ''
        : (this.isCompleted || this.isReadOnly)
          ? this.translate.instant('NO_CUSTOMER')
          : this.translate.instant('SELECT_CUSTOMER'),
      id: 'iao-contact-field',
      isReadOnly: true,
      showArrow: false,
      isRequired: isEditable && !hasContact,
      formFieldType: FormFieldType.LIST,
      isDisabled: !isEditable,
      isSearchKeyRetain: true,
      hideInlineChip,
      modalConfiguration: {
        searchTitle: this.translate.instant('SEARCH_CONTACTS'),
        selectedTitle: this.translate.instant('SELECTED_CONTACTS'),
        titleEndIcon: 'expandblue',
        searchInputConfig: {
          label: this.translate.instant('CONTACTS'),
          placeholder: this.translate.instant('TYPE') + '...',
        },
        showSearch: true,
      },
      filterMethod: this._filterContactData.bind(this),
      endIcons,
      sections,
      selectedData,
      eventHandler: (id: string, event, eventName, data) => {
        if (data && eventName === 'item-click') {
          const contact = data.refObject as Contact;
          if (contact?.ID && contact.ID !== this.allocation?.contactID) {
            this.selectContact(contact);
          }
        } else if (eventName === 'closebutton') {
          if (hasContact && data?.id !== '_iao_gate') {
            this.clearContact();
          }
        }
      },
    };
  }

  private _buildContactSourceData() {
    const selectedId = this.allocation?.contactID || '';
    this.contactSourceData = (this.contactService.contacts || [])
      .filter(c => c.isActive && c.ID !== selectedId)
      .map(contact => ({
        id: contact.ID,
        title: this._contactDisplayName(contact),
        image: '',
        refObject: contact,
      }))
      .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
  }

  private _getSelectedContactData(): any[] {
    if (!this.allocation?.contactID) return [];
    const contact = this._lookupContact(this.allocation.contactID);
    const name = contact
      ? this._contactDisplayName(contact)
      : (this.allocation.contactName || '');
    return [{
      id: this.allocation.contactID,
      title: name,
      image: '',
      refObject: contact || null,
      endIcon: 'closebutton',
    }];
  }

  private _getMeetingContactData(): any[] {
    const selectedId = this.allocation?.contactID || '';
    return (this.meetingContacts || [])
      .filter(c => c.ID !== selectedId)
      .map(c => ({
        id: c.ID,
        title: this._contactDisplayName(c),
        image: '',
        refObject: c,
      }));
  }

  private _filterContactData(query: string): any[] {
    const queryWords = query.toLowerCase().split(' ').filter(w => w.trim() !== '');
    const selectedId = this.allocation?.contactID || '';
    const matchesQuery = (title: string) => queryWords.every(word => title.toLowerCase().includes(word));

    // The 'results' section's static `data` (meetingContactData, the browse-mode view) is
    // fully replaced by this filtered set once a query is typed (showFilteredData: true) — it
    // is not merged with it. Excluding meetingIds here (as this used to) made every meeting
    // contact permanently unsearchable by name the moment the rep typed anything: cleared,
    // then re-typed, a meeting attendee (e.g. the meeting's own customer) came back as
    // "No results for this search" despite being right there on the meeting.
    const meetingMatches = (this.meetingContacts || [])
      .filter(c => c.ID !== selectedId && matchesQuery(this._contactDisplayName(c)))
      .map(c => ({ id: c.ID, title: this._contactDisplayName(c), image: '', refObject: c }));

    const meetingIdsIncluded = new Set(meetingMatches.map(m => m.id));
    const otherMatches = this.contactSourceData.filter(item =>
      item.id !== selectedId && !meetingIdsIncluded.has(item.id) && matchesQuery(item.title),
    );

    return [...meetingMatches, ...otherMatches];
  }

  private _generateContactSections(selectedSectionData: any[], meetingContactData: any[]): Section[] {
    const sections: Section[] = [
      {
        id: 'selected',
        title: this.translate.instant('SELECTED'),
        showCountOnTitle: true,
        maxResultsView: 5,
        bottomButtonShow: false,
        expand: false,
        showBottomButton: false,
        bottomButtonConfig: {},
        data: selectedSectionData,
      },
    ];

    sections.push({
      id: 'results',
      title: this.translate.instant('AL_RESULTS'),
      showFilteredData: true,
      showCountOnTitle: true,
      maxResultsView: 5,
      bottomButtonShow: true,
      expand: true,
      showBottomButton: true,
      bottomButtonConfig: {
        exceedMaxResultButton: {
          id: 'view-all',
          label: this.translate.instant('VIEW_MORE'),
        },
        lessThanResultButton: {
          id: 'show-other',
          label: this.translate.instant('SEARCH_OTHER_WITH_TEXT', { text: this.translate.instant('CUSTOMER') }),
        },
      },
      data: meetingContactData,
    });

    return sections;
  }

  onContactFormAdditionalControl(ev: any) {
    const id = typeof ev === 'string' ? ev : ev?.id;
    if (id === 'expandblue' || id === 'view-all' || id === 'show-other') {
      this.requestSelectContact();
    }
  }

  // ─── Shared Helpers ───────────────────────────────────────────────────────

  private _contactDisplayName(contact: Contact): string {
    return (contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim());
  }

  private _lookupContact(id: string): Contact | undefined {
    return this.contactService.getContactByID(id) || this.contactService.contacts.find(c => c.ID === id);
  }

  private _setupDebouncedSave() {
    this.saveSubject$.pipe(
      // Mark dirty on every request (before the debounce) so a teardown inside the
      // debounce window can detect and flush the pending save (see ngOnDestroy).
      tap(() => this._pendingSave = true),
      debounceTime(600),
      takeUntil(this.ngDestroy$),
    ).subscribe(() => {
      this._pendingSave = false;
      this._persistAllocation();
    });
  }

  // ─── Contact ──────────────────────────────────────────────────────────────

  async openContactSearch() {
    if (this.isLocked) return;
    this.isSearchingContact = true;
    this.contactSearchText = '';
    this.contactSearchResults = [...this.meetingContacts];
  }

  onContactSearch(text: string) {
    this.contactSearchText = text;
    if (!text.trim()) {
      this.contactSearchResults = this.nearMeContacts.length ? this.nearMeContacts : [...this.meetingContacts];
      return;
    }
    const lower = text.toLowerCase();
    this.contactSearchResults = this.contactService.contacts.filter(c =>
      c.fullName?.toLowerCase().includes(lower) ||
      c.firstName?.toLowerCase().includes(lower) ||
      c.lastName?.toLowerCase().includes(lower),
    ).slice(0, 20);
  }

  isNearMe(contact: Contact): boolean {
    return this.nearMeContacts.some(c => c.ID === contact.ID);
  }

  async selectContact(contact: Contact) {
    if (this.isLocked) return;
    this.allocation.contactID = contact.ID;
    this.allocation.contactName = this._contactDisplayName(contact);
    this.isSearchingContact = false;
    this.contactSearchText = '';
    this.contactSearchResults = [];
    this._removeCustomerAllocationProducts();

    const previousBaseAddressId = this.selectedAddressObj?.addressId;
    this.isTermsAccepted = false;
    this._applyAddress(null);
    this.addressLicenseChecking = true;

    // Build contact form field immediately so customer chip renders right away
    this._loadContactInfo(contact.ID);
    this._buildContactFormField();

    await this._loadAddresses(contact.ID);
    this.cd.detectChanges();
    await this.sampleDataService.syncCustomerLicensesForContact(contact.ID);
    if (!this.selectedAddressObj) {
      this._autoSelectAddressAndRefresh(previousBaseAddressId);
    } else {
      this.addressLicenseChecking = false;
      this.cd.detectChanges();
    }

    // Notify parent after addresses are loaded so meeting subject/location stay in sync
    this.onContactSelected.emit({ allocation: this.allocation, contact, address: this.selectedAddressObj });
  }

  clearContact() {
    if (this.isLocked) return;
    this.allocation.contactID = '';
    this.allocation.contactName = '';
    this._removeCustomerAllocationProducts();
    this.isTermsAccepted = false;
    this.isSearchingContact = false;
    this._buildContactFormField();
    this.cd.markForCheck();
    this.saveSubject$.next();
  }

  requestSelectContact() {
    if (this.isLocked) return;
    this.sampleService.inMeetingAllocationActivity = this.allocation;
    this.sampleService.samplingDetailsViewMode = SamplingDetailsViewMode.CREATE_FROM_MEETING;
    this.contactService.contactPageMode = ComponentViewMode.SELECTION;
    this.contactService.accessedContactListFrom = PageName.NewSampleActivityComponent;
    const callbackEvent = (data: any) => {
      if (data && data.length > 0) {
        const contact = data[0] as Contact;
        if (contact?.ID) {
          this.selectContact(contact);
        }
      } else {
        this.clearContact();
      }
    };
    this.navService.pushWithPageTracking(PageName.ContactPageComponent, PageName.ContactPageComponent, { callbackEvent }, PageName.ContactPageComponent);
  }

  clearAddress() {
    if (this.isLocked) return;
    this._applyAddress(null);
    this.isChangingAddress = true;
    this.addressSearchText = '';
    this.saveSubject$.next();
  }

  private async _loadContactInfo(contactId: string) {
    const c = this._lookupContact(contactId);
    if (!c) return;
    // Ensure allocation.contactName is always populated (needed by capture-allocation-order)
    if (!this.allocation.contactName) {
      this.allocation.contactName = this._contactDisplayName(c);
    }
  }

  getContactInitials(contact: Contact): string {
    return this._contactDisplayName(contact).split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }

  // ─── Address ──────────────────────────────────────────────────────────────

  private async _loadAddresses(contactId: string) {
    this.contactAddressList = await this.contactService.loadContactAddressesFromDB(contactId);
    this._addressStateLicenseCache.clear();
    this._prefetchAddressDropdownLicenses();
  }

  /** Apply an address to the UI + allocation model. Pass null to clear. */
  private _applyAddress(addr: ContactAddress | null, licenseValid: boolean | null = null) {
    this.selectedAddressObj = addr;
    this.selectedAddress = addr ? (addr.compositeAdd || addr.concatanatedAddress || '') : '';
    this.allocation.addressID = addr ? (addr.customerAddressID || addr.addressId) : '';
    this.allocation.location = this.selectedAddress;
    this.addressLicenseValid = licenseValid;
    this.addressStateLicenseValid = licenseValid;
  }

  private _applyExistingAddress() {
    const existing = this.contactAddressList.find(a => a.customerAddressID === this.allocation.addressID || a.addressId === this.allocation.addressID);
    if (existing) {
      this._applyAddress(existing, null);
    }
  }

  private async _resolveLockedSelectedAddress() {
    if (this.allocation?.contactID && this.allocation?.addressID) {
      if (!this.contactAddressList?.length) {
        this.contactAddressList = await this.contactService.loadContactAddressesFromDB(this.allocation.contactID);
      }
      const existing = this.contactAddressList.find(
        a => a.customerAddressID === this.allocation.addressID || a.addressId === this.allocation.addressID,
      );
      if (existing) {
        this.selectedAddressObj = existing;
        this.selectedAddress = existing.compositeAdd || existing.concatanatedAddress || '';
        if (this.selectedAddress) return;
      }
    }
    this.selectedAddress = this.allocation?.location || '';
  }

  private async _resolveLockedLicenseBadges() {
    const addr = this.selectedAddressObj;
    this.isOhioTDDDFlow = this._isTDDDApplicable(addr);
    if (!addr || !this.isOhioTDDDFlow) {
      this.addressTDDD = null;
      return;
    }

    const addressId = addr.addressId || addr.customerAddressID || '';
    if (!addressId) {
      this.addressTDDD = EMPTY_TDDD;
      return;
    }

    try {
      const categories = await this.sampleDataService.getValidTDDDCategoriesForLocation(addressId, { silent: true });
      this.addressTDDD = {
        hasCat2: categories.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat2),
        hasCat3: categories.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat3),
      };
    } catch {
      this.addressTDDD = EMPTY_TDDD;
    }
  }

  private async _autoSelectPrimaryAddress() {
    if (this.selectedAddressObj) return;
    // Prefer an address matching the parent meeting's location when the
    // meeting has an explicit address; otherwise fall back to the primary
    // (or first) contact address.
    const target = this._findAddressMatchingMeetingLocation()
      || this.contactAddressList.find(a => a.isPrimary)
      || this.contactAddressList[0];
    if (!target) return;
    // Programmatic auto-select — bypass the user-tap "pending-license" guard
    // so the chosen address renders immediately; _validateLicenseInBackground
    // handles resolving its pill.
    await this.selectAddress(target, { fromUser: false });
  }

  /**
   * If the parent meeting (ADP) has an explicit location, prefer a contact
   * address that matches it over the primary. Returns null when
   * the meeting has no usable location or no address matches.
   */
  private _findAddressMatchingMeetingLocation(): ContactAddress | null {
    if (!this.contactAddressList?.length) return null;
    const meeting = this.activityService.selectedActivity as AppointmentActivity;
    const rawLocation = meeting?.location?.trim();
    if (!rawLocation || rawLocation === 'No Location' || rawLocation === 'LiveMeet') return null;
    const normalize = (s: string) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const target = normalize(rawLocation);
    if (!target) return null;
    return this.contactAddressList.find(a => normalize(a.compositeAdd) === target || normalize(a.concatanatedAddress) === target) || null;
  }

  async selectAddress(addr: ContactAddress, opts: { fromUser?: boolean } = { fromUser: true }) {
    if (opts.fromUser && this._isAddressLicensePending(addr)) return;
    this._destroyAddressDropdown();
    const addrInput = this.addressSearchWrapEl?.nativeElement?.querySelector('input');
    if (addrInput) addrInput.blur();
    if (this.isLocked) return;
    // Same address re-selected — just close dropdown
    const currentAddrId = this.selectedAddressObj?.customerAddressID || this.selectedAddressObj?.addressId;
    const newAddrId = addr.customerAddressID || addr.addressId;
    if (currentAddrId && newAddrId && currentAddrId === newAddrId) {
      this.isChangingAddress = false;
      this.addressSearchText = '';
      return;
    }

    // Show address upfront immediately
    this._applyAddress(addr, null);
    this.addressLicenseChecking = true;
    this.isChangingAddress = false;
    this.addressSearchText = '';

    // Load eligible allocations immediately with local lot data
    this._loadEligibleAllocations();
    this._buildProductRows();
    this.cd.markForCheck();
    // Persist the address change IMMEDIATELY rather than via the 600ms debounce. The address
    // is the mandatory field for completion, and in a fast flow (pick address → complete, or
    // pick address → hand off to the signature/meeting flow) a debounced write may not have
    // reached disk yet, so a reader that reloads the order from disk sees no address. Writing
    // now makes it durable before any completion/hand-off/reload can observe the order.
    // _persistAllocation sets _persistInFlight, which _completeAllocation awaits, so completion
    // still lands after this write. Clear _pendingSave so the destroy-flush (Layer 5) doesn't
    // redundantly re-persist the same state.
    this._pendingSave = false;
    await this._persistAllocation();

    // Background: validate license and sync lots (abort previous if address changes again)
    this._validateLicenseInBackground(addr);
  }

  /** Shared: pick best address, refresh products, persist, and kick off background license check */
  private _autoSelectAddressAndRefresh(retainBaseAddressId?: string) {
    const sharedAddress = retainBaseAddressId
      ? this.contactAddressList.find(a => a.addressId === retainBaseAddressId)
      : null;

    if (this.allocation.addressID && !retainBaseAddressId) {
      this._applyExistingAddress();
    } else {
      // Priority: meeting-location match > shared (retained) address > primary > first
      const meetingMatch = this._findAddressMatchingMeetingLocation();
      const autoPick = meetingMatch
        || sharedAddress
        || this.contactAddressList.find(a => a.isPrimary)
        || this.contactAddressList[0];
      if (autoPick) {
        this._applyAddress(autoPick, null);
        this.addressLicenseChecking = true;
      } else {
        this._applyAddress(null);
        this.addressLicenseChecking = false;
      }
    }

    this._loadEligibleAllocations();
    this._buildProductRows();
    this.cd.markForCheck();
    this.saveSubject$.next();

    if (this.selectedAddressObj && this.addressLicenseValid === null) {
      this._validateLicenseInBackground(this.selectedAddressObj);
    }
  }

  private _isOhioAddress(addr: ContactAddress | null | undefined): boolean {
    if (!addr) return false;
    const name = (addr.state || '').trim().toLowerCase();
    return name === 'ohio' || name === 'oh';
  }

  private _isTDDDApplicable(addr: ContactAddress | null | undefined): boolean {
    return this._tdddCheckEnabled && this._isOhioAddress(addr);
  }

  private async _validateLicenseInBackground(addr: ContactAddress) {
    if (this.isPromoMaterial) {
      this.addressLicenseChecking = false;
      return;
    }
    if (this._licenseCheckAbort) {
      this._licenseCheckAbort.abort();
    }
    const abort = this._licenseCheckAbort = new AbortController();
    const stateId = addr.stateId || '';
    const contactId = this.allocation?.contactID || '';
    const addressId = addr.addressId || '';
    const tdddFlow = this._isTDDDApplicable(addr);
    this.isOhioTDDDFlow = tdddFlow;

    try {
      // Step 1: state-license check (always first). Drives Valid/Invalid pill.
      const isStateValid = await this.sampleDataService.hasValidStateLicense(stateId, contactId, { loadFromDbOnly: true });
      if (abort.signal.aborted) return;
      this.addressStateLicenseValid = isStateValid;

      if (!isStateValid) {
        this.addressTDDD = null;
        this.addressLicenseValid = false;
      } else if (tdddFlow) {
        // Step 2: state valid + Ohio + TDDD enabled — match TDDD by addressId.
        const categories = await this.sampleDataService.getValidTDDDCategoriesForLocation(addressId);
        if (abort.signal.aborted) return;
        const hasCat2 = categories.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat2);
        const hasCat3 = categories.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat3);
        this.addressTDDD = { hasCat2, hasCat3 };
        this.addressLicenseValid = hasCat2 || hasCat3;
      } else {
        this.addressTDDD = null;
        this.addressLicenseValid = true;
      }
    } catch {
      if (abort.signal.aborted) return;
      this.addressStateLicenseValid = true;
      this.addressLicenseValid = true;
      this.addressTDDD = tdddFlow ? EMPTY_TDDD : null;
    }
    this.addressLicenseChecking = false;
    // Refresh eligible allocations now that license result is known
    if (!abort.signal.aborted) {
      this._loadEligibleAllocations();
      this._buildProductRows();
    }
    this.cd.markForCheck();
  }

  openAddressChange() {
    if (this.isLocked) return;
    this.isChangingAddress = true;
    this.addressSearchText = '';
    this._prefetchAddressDropdownLicenses();
    this.cd.markForCheck();
    // Focus the search input and show dropdown after Angular renders it
    setTimeout(() => {
      const input = this.addressSearchWrapEl?.nativeElement?.querySelector('input');
      if (input) input.focus();
    });
  }

  onAddressSearch(text: string) {
    this.addressSearchText = text;
    this._renderAddressDropdown();
  }

  onAddressSearchFocus() {
    this.isChangingAddress = true;
    this._prefetchAddressDropdownLicenses();
    this._renderAddressDropdown();
  }

  onAddressSearchBlur() {
    setTimeout(() => {
      this._destroyAddressDropdown();
      this.isChangingAddress = false;
      this.addressSearchText = '';
      this.cd.markForCheck();
    }, 200);
  }

  get filteredAddresses(): ContactAddress[] {
    const q = (this.addressSearchText || '').toLowerCase();
    return this.contactAddressList.filter(a =>
      !q || (a.compositeAdd || '').toLowerCase().includes(q) || (a.street || '').toLowerCase().includes(q)
    );
  }

  cancelAddressChange() {
    this._destroyAddressDropdown();
    this.isChangingAddress = false;
    this.addressSearchText = '';
  }

  getAddressLine2(addr: ContactAddress): string {
    return [addr.city, addr.state, addr.postal].filter(Boolean).join(', ');
  }

  /** Ensure lots, allocations, and eligibilities are loaded from DB if not yet in memory (e.g. after app relaunch or offline revisit) */
  private async _ensureAllocationsLoaded() {
    const promises: Promise<void>[] = [];

    // Lots must be in memory for on-hand qty, lot remaining, and product eligibility checks
    if (!this.sampleService.lots?.length) {
      promises.push(this.sampleService.loadAllLotsFromDBAndMap(true));
    }

    // Customer sample allocations for allocation remaining, order limit, value limit
    if (!this.sampleService.contactCustomerSampleAllocations?.length) {
      promises.push(this.sampleService.loadAllContactCustomerAllocationsFromDBAndMap([], true));
    }

    // Customer sample product eligibilities for customer drop limit display
    if (!this.sampleService.customerSampleProductEligibilities?.length) {
      promises.push(this.sampleService.loadCustomerSampleProductEligibilities());
    }

    if (promises.length) {
      await Promise.all(promises);
    }

    // Pre-load max order quantities from local cache (avoids per-product API calls)
    if (this.hasAllocationOrdersUnrestricted && !this._maxOrderQtyMap.size) {
      try {
        const results = await this.sampleDataService.fetchAllocationsMaxOrderQuantity(undefined, true);
        this._maxOrderQtyMap.clear();
        (results || []).forEach(r => this._maxOrderQtyMap.set(r.sampleProductId, r.maxOrderQuantity));
      } catch (e) {
        console.error('Failed to load max order quantities from cache', e);
      }
    }
  }

  // ─── Products ─────────────────────────────────────────────────────────────

  private _loadEligibleAllocations() {
    // Restricted allocations require contact+address (contact-specific limits + license check).
    // Unrestricted allocations come from global SKU list — allow product search without contact/address.
    if (!this.hasAllocationOrdersUnrestricted && (!this.allocation?.contactID || !this.selectedAddress)) {
      this.eligibleAllocations = [];
      return;
    }
    const foundAllocations: CustomerSampleAllocation[] = [];

    if (this.hasAllocationOrdersUnrestricted && !this.isPromoMaterial) {
      const trackOnHand = this.hasReceiptsTab;
      this.caseManagementService.caseProductSKU
        .flatMap(prod => prod.skus)
        .forEach((sku: Brand) => {
          const alloc: CustomerSampleAllocation = <CustomerSampleAllocation>{
            sampleSKUId: sku.ID,
            sampleSKUName: sku.name,
            currencySymbol: '',
            totalsamplevaluelimit: 0,
            allowvaluelimitunblock: true,
            remaining: 0,
            onHandLotsQuantity: 0,
            sampleSKUApplicability: true,
            contactId: '',
            contactName: '',
            startDate: new Date(),
            endDate: new Date(),
            sampleDropLimitPerVisit: 0,
          };
          if (!this.sampleService.lots) return;
          let added = false;
          let onHandQty = 0;
          for (const lot of this.sampleService.lots) {
            if (!lot || lot.sampleSKUId !== sku.ID || lot.status !== 0 || !this._checkValidLot(lot)) continue;
            // OMNI-62549: keep a below-zero lot visible when it was in the rep's last physical count (AC-1).
            if (trackOnHand && lot.totalQuantityRemaining <= 0 && !this.isLotBelowZeroEligible(lot)) continue;
            if (!added) {
              foundAllocations.push(alloc);
              added = true;
            }
            if (trackOnHand) {
              // Badge reflects real positive on-hand only, so below-zero lots don't reduce it.
              if (lot.totalQuantityRemaining > 0) {
                onHandQty += lot.totalQuantityRemaining;
              }
            } else {
              break;
            }
          }
          if (trackOnHand && onHandQty > 0) {
            alloc.onHandLotsQuantity = onHandQty;
          }
        });
    } else if (this.isPromoMaterial) {
      // Promo materials: no CustomerSampleAllocation records needed — source directly
      // from position-assigned brand catalog. Accept producttype OR indskr_producttype
      // matching 268890001 — the API endpoint (/cag/v150/products) may return either
      // field depending on how Dynamics has the product configured.
      for (const brand of (this.brandService.brands ?? [])) {
        if (brand.producttype !== 268890001 && brand.indskr_producttype !== 268890001) continue;
        foundAllocations.push(this._buildPromoAllocation(brand));
      }
    } else {
      const contactAllocations = this.sampleService.contactCustomerSampleAllocations?.find(
        a => a.contactId === this.allocation.contactID
      );
      if (contactAllocations?.currentCustomerSampleAllocations) {
        contactAllocations.currentCustomerSampleAllocations.forEach(alloc => {
          const brand = this.brandService.brands?.find(b => b.ID === alloc.sampleSKUId);
          if (brand?.producttype === 268890001 || brand?.indskr_producttype === 268890001) return; // promo products handled in their own path
          if (!alloc.sampleSKUApplicability) return;
          if (!alloc.isUnlimited && alloc.remaining <= 0) return;
          // License check
          const licenseResult = this.sampleService.validateLicenses(
            alloc.id,
            alloc.customerLicenseIds || [],
            this.selectedAddressObj?.country || '',
            this.selectedAddressObj?.state || '',
            this.selectedAddressObj?.customerAddressID || '',
          );
          if (!licenseResult.isValid) return;
          // Must have at least one valid lot
          const hasValidLot = this.sampleService.lots?.some(l =>
            l.sampleSKUId === alloc.sampleSKUId && l.status === 0 && this._checkValidLot(l)
          );
          if (!hasValidLot) return;
          foundAllocations.push(structuredClone(alloc));
        });
      }
    }
    // Apply cached max order quantities so limits are available immediately (no per-product API call)
    if (this.hasAllocationOrdersUnrestricted && this._maxOrderQtyMap.size > 0) {
      foundAllocations.forEach(alloc => {
        const maxQty = this._maxOrderQtyMap.get(alloc.sampleSKUId);
        if (maxQty !== undefined) {
          alloc.sampleDropLimitPerVisit = maxQty;
        }
      });
    }
    this.eligibleAllocations = this._sortAllocationsByConfig(foundAllocations);
  }

  /**
   * Parses BU_ACTIVITY_GA_PRODUCT_SORT_ORDER config and returns a Map<GUID, order>.
   * Config JSON: [{ "id": "<guid>", "order": 1 }, ...] — matches by sampleSKUId; unmatched GUIDs ignored.
   */
  private _getSkuSortOrderMap(): Map<string, number> {
    if (this._skuSortOrderMap) return this._skuSortOrderMap;
    this._skuSortOrderMap = new Map<string, number>();
    try {
      const configValue = this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_ACTIVITY_GA_PRODUCT_SORT_ORDER);
      if (configValue && typeof configValue === 'string') {
        const parsed = JSON.parse(configValue);
        if (Array.isArray(parsed)) {
          parsed
            .filter((entry: any) => entry?.id)
            .forEach((entry: any) => {
              this._skuSortOrderMap.set(String(entry.id).trim().toLowerCase(), entry.order ?? 0);
            });
        }
      }
    } catch (e) {
      console.error('Failed to parse BU_ACTIVITY_GA_PRODUCT_SORT_ORDER config', e);
    }
    return this._skuSortOrderMap;
  }

  private _buildPromoAllocation(brand: Brand): CustomerSampleAllocation {
    const alloc = Object.create(CustomerSampleAllocation.prototype) as CustomerSampleAllocation;
    alloc.id = '';  // no indskr_customersampleproduct record exists for promo products
    alloc.sampleSKUId = brand.ID;
    alloc.sampleSKUName = brand.name;
    alloc.sampleSKUApplicability = true;
    alloc.isUnlimited = true;
    alloc.eSignatureNecessary = false;
    alloc.customerLicenseIds = [];
    alloc.sampleDropLimitPerVisit = 0;
    alloc.allowvaluelimitunblock = true;
    alloc.totalsamplevaluelimit = 0;
    alloc.indskr_totalsamplevaluedelivered = 0;
    alloc.buProductSampling = false;
    alloc.onHandLotsQuantity = 0;
    alloc.currencySymbol = '';
    alloc.allocationName = '';
    alloc.buId = '';
    alloc.buName = '';
    alloc.skuCurrencyId = '';
    alloc.allocCurrencyId = '';
    alloc.allocationRulesProfileId = '';
    return alloc;
  }

  private _sortAllocationsByConfig(allocations: CustomerSampleAllocation[]): CustomerSampleAllocation[] {
    const configOrder = this._getSkuSortOrderMap();
    return allocations.sort((a, b) => {
      if (configOrder.size > 0) {
        const aIdx = configOrder.has(a.sampleSKUId?.toLowerCase());
        const bIdx = configOrder.has(b.sampleSKUId?.toLowerCase());
        if (aIdx && bIdx) {
          return configOrder.get(a.sampleSKUId.toLowerCase()) - configOrder.get(b.sampleSKUId.toLowerCase());
        }
        if (aIdx) return -1;
        if (bIdx) return 1;
      }
      return (a.sampleSKUName || '').localeCompare(b.sampleSKUName || '');
    });
  }

  /**
   * OMNI-62549: loads the set of lots eligible for below-zero sampling (present in the last physical
   * count with qty > 0). Gated on RECIEPTS_TAB; reads the locally-persisted set (saved at reconciliation
   * submit), so it works identically online and offline. Empty when the feature is off (on-hand gating).
   */
  private async loadLastPhysicalCountEligibleLots(): Promise<void> {
    // The reconciliation-based below-zero check applies only when the RECIEPTS_TAB + SAMPLE_RECONCILIATIONS
    // feature actions AND the BU_ALLOCATIONORDER_GA_ALLOWBELOWZERODROP ioConfig are all on; otherwise
    // eligibility falls back to on-hand behavior.
    if (!this.hasReceiptsTab
      || !this.authService.hasFeatureAction(FeatureActionsMap.SAMPLE_RECONCILIATIONS)
      || !this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_ALLOWBELOWZERODROP)) {
      this.lastPhysicalCountEligibleLotIds = new Set<string>();
      return;
    }
    try {
      this.lastPhysicalCountEligibleLotIds = await this.sampleReconciliationService.getLastPhysicalCountEligibleLotIds();
    } catch (error) {
      console.error('Error loading last physical count eligible lots:', error);
      this.lastPhysicalCountEligibleLotIds = new Set<string>();
    }
  }

  /**
   * OMNI-62549: true when a lot may be sampled even though its on-hand qty is at/below zero, because it
   * was in the rep's last physical count. Only ever true when RECIEPTS_TAB is on (the set is otherwise
   * empty), so RECIEPTS_TAB-off behavior is unchanged.
   */
  /** OMNI-62549: the below-zero sampling feature is on only with all three gates. */
  private get isBelowZeroSamplingEnabled(): boolean {
    return this.hasReceiptsTab
      && this.authService.hasFeatureAction(FeatureActionsMap.SAMPLE_RECONCILIATIONS)
      && !!this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_ALLOWBELOWZERODROP);
  }

  private isLotBelowZeroEligible(lot: Lot): boolean {
    return !!lot && this.isLotIdBelowZeroEligible(lot.id);
  }

  /** OMNI-62549: id-based variant for stores that key lots by lotId (e.g. InlineLotRow). */
  private isLotIdBelowZeroEligible(lotId: string): boolean {
    return this.isBelowZeroSamplingEnabled && !!lotId && this.lastPhysicalCountEligibleLotIds.has(lotId);
  }

  private _checkValidLot(lot: Lot): boolean {
    const orderDateByUser = this.allocation?.orderDateByUser ? new Date(this.allocation.orderDateByUser) : null;
    const today = new Date();
    const isOrderDateInFuture = orderDateByUser && isValid(orderDateByUser) ? isAfter(orderDateByUser, today) : false;

    if (lot.lastdispatchdate && lot.lastdispatchdate !== 'Invalid Date') {
      if (isOrderDateInFuture) {
        return isBefore(orderDateByUser, new Date(lot.lastdispatchdate));
      }
      return isAfter(new Date(lot.lastdispatchdate), today);
    } else if (this.sampleLastDispatchLimitNum > 0 && lot.validTo) {
      const calculatedDate = subDays(new Date(lot.validTo), this.sampleLastDispatchLimitNum);
      if (calculatedDate && isValid(calculatedDate)) {
        if (isOrderDateInFuture) {
          return isBefore(orderDateByUser, calculatedDate);
        }
        return isAfter(calculatedDate, today);
      }
    } else if (lot.validTo && isValid(new Date(lot.validTo))) {
      if (isOrderDateInFuture) {
        return isBefore(orderDateByUser, new Date(lot.validTo));
      }
      return isAfter(new Date(lot.validTo), today);
    }
    return false;
  }

  onProductSearchInput(text: string) {
    this.productSearchText = text;
    this._filterProductResults(text);
    this._renderProductDropdown();
  }

  onProductSearchFocus() {
    if (this._isMobileNative()) {
      const input = this.productSearchWrapEl?.nativeElement?.querySelector('input');
      if (input) input.blur();
      this.productSearchText = '';
      this._filterProductResults('');
      this._renderProductDropdown();
      setTimeout(() => this._productDropdownSearchInputEl?.focus({ preventScroll: true }));
      return;
    }
    this._filterProductResults(this.productSearchText);
    this._renderProductDropdown();
  }

  onProductSearchBlur() {
    if (this._isMobileNative()) return;
    setTimeout(() => {
      this._destroyProductDropdown();
      this.isSearchingProduct = false;
      this.cd.markForCheck();
    }, 200);
  }

  /** Core: get eligible allocations with their unadded lots, optionally filtered by search text */
  private _getFilteredAllocations(searchText = ''): { alloc: CustomerSampleAllocation; lots: Lot[] }[] {
    const lower = searchText.trim().toLowerCase();
    const addedLotIds = new Set<string>();
    const addedSkuIds = new Set<string>();
    const removingSkuIds = new Set<string>();
    this.productRows.forEach(r => {
      r.lots.forEach(l => addedLotIds.add(l.lotId));
      addedSkuIds.add(r.skuId);
      if (r.isRemoving) removingSkuIds.add(r.skuId);
    });

    const results: { alloc: CustomerSampleAllocation; lots: Lot[] }[] = [];
    this.eligibleAllocations.forEach(alloc => {
      if (removingSkuIds.has(alloc.sampleSKUId)) return;

      if (this.isPromoMaterial) {
        // Promo products have no lots — bypass lot check; exclude already-added SKUs
        if (addedSkuIds.has(alloc.sampleSKUId)) return;
        if (lower && !alloc.sampleSKUName.toLowerCase().includes(lower)) return;
        results.push({ alloc, lots: [] });
        return;
      }

      const unaddedLots = this._getLotsForDropdown(alloc).filter(l => !addedLotIds.has(l.id));
      if (!unaddedLots.length) return;
      if (lower) {
        const skuMatch = alloc.sampleSKUName.toLowerCase().includes(lower);
        const lotMatch = unaddedLots.some(l =>
          (l.name || '').toLowerCase().includes(lower) || (l.lotNumber || '').toLowerCase().includes(lower)
        );
        if (!skuMatch && !lotMatch) return;
      }
      results.push({ alloc, lots: unaddedLots });
    });
    return results;
  }

  /** Build flat item array for product modal (initial + filtered) */
  private _buildProductModalItems(text = ''): any[] {
    const lower = text.trim().toLowerCase();
    const items: any[] = [];
    this._getFilteredAllocations(text).forEach(({ alloc, lots }) => {
      const skuOnHand = this.hideSystemOnHandQuantity ? 0 : alloc.onHandLotsQuantity;
      items.push({
        id: `sku_${alloc.sampleSKUId}`,
        title: this.isPromoMaterial ? alloc.sampleSKUName : `${alloc.sampleSKUName} (${alloc.remaining || skuOnHand || 0})`,
        image: '',
        refObject: { type: 'sku', alloc },
      });
      lots.forEach(lot => {
        if (lower && !(lot.name || '').toLowerCase().includes(lower)
            && !(lot.lotNumber || '').toLowerCase().includes(lower)
            && !alloc.sampleSKUName.toLowerCase().includes(lower)) return;
        const lotTitle = this.hideSystemOnHandQuantity
          ? `  ${lot.lotNumber || lot.name}`
          : `  ${lot.lotNumber || lot.name} (${lot.totalQuantityRemaining || 0})`;
        items.push({
          id: `lot_${lot.id}`,
          title: lotTitle,
          image: '',
          refObject: { type: 'lot', alloc, lotId: lot.id },
        });
      });
    });
    return items;
  }


  private _filterProductResults(text: string) {
    const filtered = this._getFilteredAllocations(text);
    this.productSearchResults = filtered.map(r => r.alloc);
    this.isSearchingProduct = this.productSearchResults.length > 0;
  }

  /** Create / update a dropdown overlay appended to document.body (escapes ion-content contain) */
  private _renderProductDropdown() {
    if (this._isMobileNative()) {
      this._renderMobileProductDropdown();
      return;
    }

    if (!this.productSearchResults.length || !this.productSearchWrapEl?.nativeElement) {
      this._destroyProductDropdown();
      return;
    }
    const rect = this.productSearchWrapEl.nativeElement.getBoundingClientRect();

    if (!this._productDropdownEl) {
      this._productDropdownEl = this.renderer.createElement('div');
      this._productDropdownEl.className = 'iao-body-dropdown';
      this.renderer.appendChild(document.body, this._productDropdownEl);
    }

    // Position
    const s = this._productDropdownEl.style;
    s.position = 'fixed';
    s.top = `${rect.bottom + 4}px`;
    s.left = `${rect.left}px`;
    s.width = `${rect.width}px`;
    s.zIndex = '99999';
    s.background = '#ffffff';
    s.border = '1px solid #e5e5ea';
    s.borderRadius = '4px';
    s.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
    s.maxHeight = '260px';
    s.overflowY = 'auto';
    (s as any)['-webkit-overflow-scrolling'] = 'touch';

    this._productDropdownEl.innerHTML = this._buildProductDropdownItemsHtml();
    this._attachProductDropdownItemHandlers();
    this._setParentScroll(false);
  }

  private _buildProductDropdownItemsHtml(): string {
    const addedLotIds = new Set<string>();
    this.productRows.forEach(r => r.lots.forEach(l => addedLotIds.add(l.lotId)));

    const controlledPillStyle = 'display:inline-block;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:500;background:rgba(255,59,48,0.1);color:#ff3b30;margin-right:4px;';
    const controlledLabel = this.translate.instant('CONTROLLED');

    let html = '';
    this.productSearchResults.forEach((alloc, i) => {
      const skuLots = this._getLotsForDropdown(alloc).filter(l => !addedLotIds.has(l.id));
      const skuHasControlled = this._getSkuControlledState(alloc.sampleSKUId, skuLots.some(l => !!l.isControlled));

      let pills = '';
      if (skuHasControlled) {
        pills += `<span style="${controlledPillStyle}">${controlledLabel}</span>`;
      }
      if (alloc.isUnlimited && !this.isPromoMaterial) {
        pills += `<span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:500;background:rgba(52,199,89,0.1);color:#34c759;margin-right:4px;">${this.translate.instant('UNLIMITED')}</span>`;
      }
      if (alloc.totalsamplevaluelimit > 0) {
        const remaining = alloc.totalsamplevaluelimit - (alloc.indskr_totalsamplevaluedelivered || 0);
        pills += `<span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:500;background:rgba(0,122,255,0.06);color:#007aff;margin-right:4px;">${this.translate.instant('VALUE_REMAINING')}: ${alloc.currencySymbol || ''}${remaining}</span>`;
      }
      const remainingPill = !alloc.isUnlimited && alloc.remaining > 0
        ? `<span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:500;background:#f2f2f7;color:#6e6e73;margin-right:4px;">${this.translate.instant('REMAINING')}: ${alloc.remaining}</span>`
        : '';

      let lotsHtml = '';
      if (skuLots.length) {
        lotsHtml = '<div style="border-top:1px solid #f2f2f7;">';
        skuLots.forEach(lot => {
          const lotLabel = (this.hasReceiptsTab && !this.hideSystemOnHandQuantity)
            ? `${lot.name} (${lot.totalQuantityRemaining})`
            : lot.name;
          const dateLabel = this._getDateInfoLabelForLot(lot);
          lotsHtml += `<div class="iao-body-dropdown-lot" data-alloc-index="${i}" data-lot-id="${lot.id}" style="padding:6px 12px 6px 24px;display:flex;align-items:flex-start;gap:6px;cursor:pointer;" onmouseover="this.style.background='#f2f2f7'" onmouseout="this.style.background='transparent'">
            <span style="color:#6e6e73;font-size:12px;margin-top:1px;">&#8226;</span>
            <div style="flex:1;min-width:0;">
              <div style="font-size:13px;color:#1a1a1a;font-weight:400;">${lotLabel}</div>
              ${dateLabel ? `<div style="font-size:11px;color:#8e8e93;margin-top:2px;">${dateLabel}</div>` : ''}
            </div>
          </div>`;
        });
        lotsHtml += '</div>';
      }

      html += `<div style="border-bottom:1px solid #e5e5ea;">
        <div class="iao-body-dropdown-option" data-index="${i}" style="cursor:pointer;padding:10px 12px;" onmouseover="this.style.background='#f2f2f7'" onmouseout="this.style.background='transparent'">
          <div style="display:flex;align-items:center;justify-content:space-between;">
            <span style="font-size:14px;font-weight:500;color:#1a1a1a;">${alloc.sampleSKUName || ''}${(alloc.onHandLotsQuantity > 0 && !this.hideSystemOnHandQuantity) ? ` <span style="font-weight:400;color:#8e8e93;">(${alloc.onHandLotsQuantity})</span>` : ''}</span>
          </div>
          <div style="margin-top:4px;display:flex;flex-wrap:wrap;gap:2px;">${pills}${remainingPill}</div>
        </div>
        ${lotsHtml}
      </div>`;
    });
    return html;
  }

  private _attachProductDropdownItemHandlers() {
    if (!this._productDropdownEl) return;

    const skuHeaders = this._productDropdownEl.querySelectorAll('.iao-body-dropdown-option');
    skuHeaders.forEach((el: HTMLElement) => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const idx = parseInt(el.getAttribute('data-index'), 10);
        if (!isNaN(idx) && this.productSearchResults[idx]) {
          this.ngZone.run(() => this.selectProduct(this.productSearchResults[idx]));
        }
      });
    });

    const lotRows = this._productDropdownEl.querySelectorAll('.iao-body-dropdown-lot');
    lotRows.forEach((el: HTMLElement) => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const allocIdx = parseInt(el.getAttribute('data-alloc-index'), 10);
        const lotId = el.getAttribute('data-lot-id');
        if (!isNaN(allocIdx) && this.productSearchResults[allocIdx] && lotId) {
          this.ngZone.run(() => this.selectLot(this.productSearchResults[allocIdx], lotId));
        }
      });
    });
  }

  private _destroyProductDropdown() {
    if (this._productDropdownResizeHandler) {
      window.removeEventListener('resize', this._productDropdownResizeHandler);
      window.visualViewport?.removeEventListener('resize', this._productDropdownResizeHandler);
      window.visualViewport?.removeEventListener('scroll', this._productDropdownResizeHandler);
      this._productDropdownResizeHandler = null;
    }
    if (this._productDropdownOutsideClickHandler) {
      document.removeEventListener('mousedown', this._productDropdownOutsideClickHandler, true);
      document.removeEventListener('touchstart', this._productDropdownOutsideClickHandler, true);
      this._productDropdownOutsideClickHandler = null;
    }
    if (this._productDropdownEl) {
      this._productDropdownEl.remove();
      this._productDropdownEl = null;
    }
    this._productDropdownSearchInputEl = null;
    this._productDropdownIsMobile = false;
    this._setParentScroll(true);
  }

  /** Mobile native (iPhone / iPad / Android, not Electron) → body-appended dropdown with bottom search. */
  private _isMobileNative(): boolean {
    const f = this.device.deviceFlags;
    return !f?.electron && (f?.android || f?.iPhone || f?.iPad);
  }

  /** Render the mobile dropdown: scroll panel of SKUs/lots + bottom search input. */
  private _renderMobileProductDropdown() {
    if (!this.productSearchWrapEl?.nativeElement) {
      this._destroyProductDropdown();
      return;
    }

    if (!this._productDropdownEl) {
      this._productDropdownEl = this.renderer.createElement('div');
      this._productDropdownEl.className = 'iao-body-dropdown iao-body-dropdown--mobile';
      this.renderer.appendChild(document.body, this._productDropdownEl);
      this._productDropdownIsMobile = true;

      this._productDropdownResizeHandler = () => this._positionMobileProductDropdown();
      this._productDropdownOutsideClickHandler = (e: Event) => this._onMobileProductDropdownOutsideClick(e);
      window.addEventListener('resize', this._productDropdownResizeHandler);
      window.visualViewport?.addEventListener('resize', this._productDropdownResizeHandler);
      window.visualViewport?.addEventListener('scroll', this._productDropdownResizeHandler);
      document.addEventListener('mousedown', this._productDropdownOutsideClickHandler, true);
      document.addEventListener('touchstart', this._productDropdownOutsideClickHandler, true);
    }

    const itemsHtml = this._buildProductDropdownItemsHtml();
    this._productDropdownEl.innerHTML = `
      <div class="iao-body-dropdown-list">${
        itemsHtml || `<div class="iao-body-dropdown-empty">${this.translate.instant('NO_RESULTS')}</div>`
      }</div>
      <div class="iao-body-dropdown-search">
        <div class="iao-product-search-wrap">
          <ion-icon name="search-outline" class="iao-search-icon"></ion-icon>
          <input type="text"
                 class="iao-product-search-input iao-body-dropdown-search-input"
                 placeholder="${this._escapeAttr(this.translate.instant('ADD_PRODUCT'))}"
                 value="${this._escapeAttr(this.productSearchText)}"
                 autocomplete="off"
                 autocorrect="off"
                 spellcheck="false" />
          <button type="button" class="iao-cancel-btn iao-body-dropdown-clear"${
            this.productSearchText ? '' : ' style="display:none;"'
          }>
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>
      </div>
    `;

    this._productDropdownSearchInputEl =
      this._productDropdownEl.querySelector('.iao-body-dropdown-search-input') as HTMLInputElement;
    this._productDropdownSearchInputEl?.addEventListener('input', (e: Event) => {
      const val = (e.target as HTMLInputElement).value || '';
      this.ngZone.run(() => {
        this.productSearchText = val;
        this._filterProductResults(val);
        this._updateMobileDropdownList();
      });
    });

    const clearBtn = this._productDropdownEl.querySelector('.iao-body-dropdown-clear') as HTMLElement;
    clearBtn?.addEventListener('mousedown', (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.ngZone.run(() => {
        this.productSearchText = '';
        this._filterProductResults('');
        if (this._productDropdownSearchInputEl) {
          this._productDropdownSearchInputEl.value = '';
          this._productDropdownSearchInputEl.focus();
        }
        this._updateMobileDropdownList();
      });
    });

    this._positionMobileProductDropdown();
    this._attachProductDropdownItemHandlers();
    this._setParentScroll(false);
  }

  /** Re-render only the list portion (preserves the bottom search input + its focus). */
  private _updateMobileDropdownList() {
    if (!this._productDropdownEl || !this._productDropdownIsMobile) return;
    const listEl = this._productDropdownEl.querySelector('.iao-body-dropdown-list') as HTMLElement;
    if (listEl) {
      listEl.innerHTML = this._buildProductDropdownItemsHtml()
        || `<div class="iao-body-dropdown-empty">${this.translate.instant('NO_RESULTS')}</div>`;
      this._attachProductDropdownItemHandlers();
    }
    const clearBtn = this._productDropdownEl.querySelector('.iao-body-dropdown-clear') as HTMLElement;
    if (clearBtn) {
      clearBtn.style.display = this.productSearchText ? '' : 'none';
    }
  }

  private _positionMobileProductDropdown() {
    if (!this._productDropdownEl) return;
    const wrapEl = this.productSearchWrapEl?.nativeElement;
    if (!wrapEl) return;

    const visualVp = window.visualViewport;
    const viewportH = visualVp?.height ?? window.innerHeight;
    const viewportTop = visualVp?.offsetTop ?? 0;
    const keyboardH = Math.max(0, window.innerHeight - (viewportTop + viewportH));
    const safeAreaTop = this._readSafeAreaInsetTop() + 50; // tap zone above the dropdown
    const minH = 180;
    const availableH = window.innerHeight - keyboardH - safeAreaTop;

    const rect = wrapEl.getBoundingClientRect();

    const s = this._productDropdownEl.style;
    s.position = 'fixed';
    s.left = `${rect.left}px`;
    s.width = `${rect.width}px`;
    s.right = 'auto';
    s.top = 'auto';
    s.bottom = `${keyboardH}px`;
    s.maxHeight = `${Math.max(minH, availableH)}px`;
    s.zIndex = '999999';
  }

  private _onMobileProductDropdownOutsideClick(e: Event) {
    const t = e.target as HTMLElement;
    if (!t) return;
    if (this._productDropdownEl?.contains(t)) return;
    if (this.productSearchWrapEl?.nativeElement?.contains(t)) return;
    this.ngZone.run(() => {
      this._destroyProductDropdown();
      this.productSearchText = '';
      this.productSearchResults = [];
      this.isSearchingProduct = false;
      this.cd.markForCheck();
    });
  }

  private _readSafeAreaInsetTop(): number {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;left:0;height:env(safe-area-inset-top, 0px);visibility:hidden;pointer-events:none;';
    document.body.appendChild(probe);
    const inset = probe.getBoundingClientRect().height;
    probe.remove();
    return inset;
  }

  private _escapeAttr(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ─── Quantity picker dropdown ─────────────────────────────────────────────
  private _qtyDropdownEl: HTMLElement | null = null;
  private _qtyDropdownCleanup: (() => void) | null = null;

  openQuantityDropdown(event: Event, product: InlineProductRow, lot: InlineLotRow) {
    event.stopPropagation();
    if (product.isPending) return;
    // Toggle: close if already open for the same lot
    if (this._qtyDropdownEl) {
      this._destroyQuantityDropdown();
      return;
    }
    const btn = (event.target as HTMLElement).closest('.iao-qty-dropdown-btn') as HTMLElement;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const max = this.getMaxQuantityForLot(product, lot);

    this._setParentScroll(false);
    this._qtyDropdownEl = this.renderer.createElement('div');
    this.renderer.appendChild(document.body, this._qtyDropdownEl);

    const s = this._qtyDropdownEl.style;
    s.position = 'fixed';
    s.width = '56px';
    s.left = `${rect.left - 40}px`;

    // Open upward if not enough space below
    const dropdownMaxH = 240;
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    if (spaceBelow < dropdownMaxH && rect.top > spaceBelow) {
      s.bottom = `${window.innerHeight - rect.top + 4}px`;
    } else {
      s.top = `${rect.bottom + 4}px`;
    }
    s.zIndex = '99999';
    s.background = '#ffffff';
    s.border = '1px solid #e5e5ea';
    s.borderRadius = '4px';
    s.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
    s.maxHeight = '240px'; // ~10 items visible
    s.overflowY = 'auto';
    (s as any)['-webkit-overflow-scrolling'] = 'touch';
    s.scrollbarWidth = 'none';
    // Hide scrollbar across browsers
    const styleTag = document.createElement('style');
    styleTag.textContent = '.iao-qty-dd-container::-webkit-scrollbar { display: none; }';
    document.head.appendChild(styleTag);
    this._qtyDropdownEl.classList.add('iao-qty-dd-container');
    this._qtyDropdownEl['_styleTag'] = styleTag;

    let html = '';
    for (let i = 1; i <= max; i++) {
      const isActive = i === lot.quantity;
      html += `<div class="iao-qty-dd-option" data-value="${i}" style="padding:6px 0;text-align:center;font-size:14px;font-weight:${isActive ? '600' : '400'};color:${isActive ? '#007aff' : '#1a1a1a'};background:${isActive ? 'rgba(0,122,255,0.08)' : 'transparent'};cursor:pointer;">${i}</div>`;
    }
    this._qtyDropdownEl.innerHTML = html;

    // Scroll to current value
    const activeOpt = this._qtyDropdownEl.querySelector(`[data-value="${lot.quantity}"]`) as HTMLElement;
    if (activeOpt) {
      activeOpt.scrollIntoView({ block: 'center' });
    }

    // Attach click handlers
    const options = this._qtyDropdownEl.querySelectorAll('.iao-qty-dd-option');
    options.forEach((el: HTMLElement) => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const val = parseInt(el.getAttribute('data-value'), 10);
        if (!isNaN(val)) {
          this.ngZone.run(() => {
            const validationError = this._validateProductQuantity(product, lot, val);
            if (validationError) {
              lot.quantity = val;
              product.errorMessage = validationError;
              product.isQuantityInvalid = true;
            } else {
              lot.quantity = val;
              product.errorMessage = '';
              product.isQuantityInvalid = false;
              product.isValueLimitExceeded = false;
              this._syncProductRowsToAllocation();
              this._recalcProductMeta(product);
              this.saveSubject$.next();
            }
            this.cd.markForCheck();
            this._destroyQuantityDropdown();
          });
        }
      });
      // Hover effect
      el.addEventListener('mouseover', () => { if (parseInt(el.getAttribute('data-value'), 10) !== lot.quantity) el.style.background = '#f2f2f7'; });
      el.addEventListener('mouseout', () => { if (parseInt(el.getAttribute('data-value'), 10) !== lot.quantity) el.style.background = 'transparent'; });
    });

    // Close on outside click
    const outsideHandler = (e: MouseEvent) => {
      if (this._qtyDropdownEl && !this._qtyDropdownEl.contains(e.target as Node)) {
        this._destroyQuantityDropdown();
      }
    };
    setTimeout(() => document.addEventListener('mousedown', outsideHandler));
    this._qtyDropdownCleanup = () => document.removeEventListener('mousedown', outsideHandler);
  }

  private _destroyQuantityDropdown() {
    if (this._qtyDropdownEl) {
      if (this._qtyDropdownEl['_styleTag']) {
        this._qtyDropdownEl['_styleTag'].remove();
      }
      this._qtyDropdownEl.remove();
      this._qtyDropdownEl = null;
    }
    if (this._qtyDropdownCleanup) {
      this._qtyDropdownCleanup();
      this._qtyDropdownCleanup = null;
    }
    this._setParentScroll(true);
  }

  // ─── Address body-appended overlay ────────────────────────────────────────

  private _stateLicensePillsApplicable(): boolean {
    if (this.isPromoMaterial) return false;
    if (!this.hasAllocationOrdersUnrestricted) return false;
    const v = this.authService.getIOConfigurationValue(
      IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_STATELICENSENECESSARYCHECK,
    );
    return v === 'Yes';
  }

  private _licensePillsApplicable(): boolean {
    if (this.isPromoMaterial) return false;
    return this._stateLicensePillsApplicable() || this._tdddCheckEnabled;
  }

  private _addressLicenseCacheKey(stateId: string): string {
    return `${this.allocation?.contactID || ''}::${stateId}`;
  }

  private _prefetchAddressDropdownLicenses() {
    if (!this._licensePillsApplicable() || !this.allocation?.contactID) return;
    const gen = ++this._addressLicensePrefetchGen;
    const contactId = this.allocation.contactID;
    const stateLicenseApplicable = this._stateLicensePillsApplicable();

    const inputs: { stateId?: string; addressId?: string }[] = [];
    const seenStates = new Set<string>();
    const tdddAddressIds: string[] = [];
    for (const a of this.contactAddressList) {
      const sid = (a.stateId || '').trim();
      if (stateLicenseApplicable && sid && !seenStates.has(sid)) {
        seenStates.add(sid);
        const stateKey = this._addressLicenseCacheKey(sid);
        if (!this._addressStateLicenseCache.has(stateKey)) inputs.push({ stateId: sid });
      }
      if (this._isTDDDApplicable(a)) {
        const aid = (a.addressId || '').trim();
        if (aid && !this._addressTDDDCache.has(aid)) {
          inputs.push({ addressId: aid });
          tdddAddressIds.push(aid);
        }
      }
    }
    if (!inputs.length) return;

    void (async () => {
      try {
        const result = await this.sampleDataService.validateAddressesFromDB(inputs, contactId);
        if (gen !== this._addressLicensePrefetchGen) return;
        result.stateValidByStateId.forEach((valid, stateId) => {
          this._addressStateLicenseCache.set(this._addressLicenseCacheKey(stateId), valid);
        });
        result.tdddCategoriesByAddressId.forEach((cats, addressId) => {
          this._addressTDDDCache.set(addressId, {
            hasCat2: cats.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat2),
            hasCat3: cats.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat3),
          });
        });
      } catch {
        if (gen !== this._addressLicensePrefetchGen) return;
        seenStates.forEach(stateId => {
          const key = this._addressLicenseCacheKey(stateId);
          if (!this._addressStateLicenseCache.has(key)) this._addressStateLicenseCache.set(key, true);
        });
        tdddAddressIds.forEach(addressId => {
          if (!this._addressTDDDCache.has(addressId)) this._addressTDDDCache.set(addressId, EMPTY_TDDD);
        });
      }
      this.ngZone.run(() => this.cd.markForCheck());
    })();
  }

  _isAddressLicensePending(addr: ContactAddress): boolean {
    if (!this._licensePillsApplicable()) return false;
    const stateId = (addr?.stateId || '').trim();
    const stateApplicable = this._stateLicensePillsApplicable();
    if (stateApplicable) {
      if (!stateId) return false;
      const stateKey = this._addressLicenseCacheKey(stateId);
      if (!this._addressStateLicenseCache.has(stateKey)) return true;
      // State invalid → row is decided; no TDDD pill will render.
      if (this._addressStateLicenseCache.get(stateKey) === false) return false;
    }
    if (stateId && this._isTDDDApplicable(addr)) {
      if (!this._addressTDDDCache.has((addr.addressId || '').trim())) return true;
    }
    return false;
  }

  private _renderAddressDropdown() {
    const wrapEl = this.addressSearchWrapEl?.nativeElement;
    if (!this.filteredAddresses.length || !wrapEl || !this.addressDropdownTpl) {
      this._destroyAddressDropdown();
      return;
    }

    if (!this._addressDropdownEl) {
      this._addressDropdownEl = this.renderer.createElement('div') as HTMLElement;
      this._addressDropdownEl.className = 'iao-body-dropdown';
      this.renderer.appendChild(document.body, this._addressDropdownEl);
    }
    if (!this._addressDropdownView) {
      this._addressDropdownView = this.addressDropdownTpl.createEmbeddedView(null);
      this.appRef.attachView(this._addressDropdownView);
      this._addressDropdownView.rootNodes.forEach(n => this._addressDropdownEl.appendChild(n));
    }

    const rect = wrapEl.getBoundingClientRect();
    const s = this._addressDropdownEl.style;
    s.top = `${rect.bottom + 4}px`;
    s.left = `${rect.left}px`;
    s.width = `${rect.width}px`;

    this._setParentScroll(false);
  }

  private _destroyAddressDropdown() {
    if (this._addressDropdownView) {
      this.appRef.detachView(this._addressDropdownView);
      this._addressDropdownView.destroy();
      this._addressDropdownView = null;
    }
    if (this._addressDropdownEl) {
      this._addressDropdownEl.remove();
      this._addressDropdownEl = null;
    }
    this._setParentScroll(true);
  }

  // ─── Template helpers (consumed by `#addressDropdownTpl`) ─────────────────

  trackAddressOption = (_: number, addr: ContactAddress) =>
    addr?.addressId || addr?.customerAddressID || '';

  addressOptionLine1(addr: ContactAddress): string {
    return addr?.street || addr?.compositeAdd || '';
  }

  addressOptionLine2(addr: ContactAddress): string {
    return [addr?.city, addr?.state, addr?.postal].filter(Boolean).join(', ');
  }

  /** Pill state for one dropdown row, derived from the prefetch caches.
   *  `null` for state/tddd means the pill should be hidden. */
  addressOptionPillState(addr: ContactAddress): {
    primary: boolean;
    state: 'loading' | 'valid' | 'invalid' | null;
    tddd: 'loading' | 'cat3' | 'cat2' | 'invalid' | null;
  } {
    const out = {
      primary: !!addr?.isPrimary,
      state: null as 'loading' | 'valid' | 'invalid' | null,
      tddd: null as 'loading' | 'cat3' | 'cat2' | 'invalid' | null,
    };
    if (!this._licensePillsApplicable()) return out;

    const stateId = (addr?.stateId || '').trim();
    const stateApplicable = this._stateLicensePillsApplicable();
    let stateOk: boolean | undefined;
    if (stateApplicable) {
      if (!stateId) {
        out.state = 'invalid';
        stateOk = false;
      } else {
        const cached = this._addressStateLicenseCache.get(this._addressLicenseCacheKey(stateId));
        if (cached === undefined) {
          out.state = 'loading';
        } else {
          stateOk = cached;
          out.state = cached ? 'valid' : 'invalid';
        }
      }
    }

    const showTDDD = this._isTDDDApplicable(addr) && (!stateApplicable || stateOk === true);
    if (showTDDD) {
      const result = this._addressTDDDCache.get((addr?.addressId || '').trim());
      if (!result) out.tddd = 'loading';
      else if (result.hasCat3) out.tddd = 'cat3';
      else if (result.hasCat2) out.tddd = 'cat2';
      else out.tddd = 'invalid';
    }

    return out;
  }

  onAddressOptionMousedown(event: MouseEvent, addr: ContactAddress) {
    event.preventDefault();
    // Block commit while this row's state-license pill is still resolving;
    // styling (cursor:progress + opacity) signals the row is unclickable.
    if (this._isAddressLicensePending(addr)) return;
    this.selectAddress(addr);
  }

  // ─── Parent scroll lock ───────────────────────────────────────────────────
  private _setParentScroll(enabled: boolean) {
    if (!this._parentIonContent) {
      this._parentIonContent = this.elRef.nativeElement.closest('ion-content') as HTMLIonContentElement;
    }
    if (this._parentIonContent) {
      this._parentIonContent.scrollY = enabled;
    }
  }

  /**
   * Select entire SKU — adds all unselected lots. If the SKU already has a row in the table,
   * the new lots are merged into the existing row (grouped).
   */
  selectProduct(alloc: CustomerSampleAllocation) {
    this._clearProductSearch();

    if (this.isPromoMaterial) {
      // Promo products have no real lots — use a single synthetic lot row for quantity entry
      const promoLotRow: InlineLotRow = {
        lotId: '',  // no real lot record for promo — omit lot ID from CRM payload
        lotName: '',
        expiryDisplay: '',
        lastDispatchDisplay: '',
        quantity: 0,
        unitPrice: 0,
        totalQuantityRemaining: 0,
        lotRefData: null,
        activityLotRefData: null,
      };
      this._addLotsToTable(alloc, [promoLotRow]);
      return;
    }

    // Only include lots NOT already in the table
    const addedLotIds = new Set<string>();
    this.productRows.forEach(r => r.lots.forEach(l => addedLotIds.add(l.lotId)));

    const validLots = (this.sampleService.lots || [])
      .filter(l => l.sampleSKUId === alloc.sampleSKUId && l.status === 0 && this._checkValidLot(l)
        // OMNI-62549: include below-zero lots from the last physical count (AC-1).
        && (!this.hasReceiptsTab || l.totalQuantityRemaining > 0 || this.isLotBelowZeroEligible(l))
        && !addedLotIds.has(l.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    if (!validLots.length) return;

    const lotRows: InlineLotRow[] = validLots.map(lot => this._buildLotRow(lot));
    this._addLotsToTable(alloc, lotRows);
  }

  /**
   * Select a single lot from the dropdown. If the SKU already has a row in the table,
   * the lot is merged into the existing row (grouped).
   */
  selectLot(alloc: CustomerSampleAllocation, lotId: string) {
    this._clearProductSearch();

    const lot = (this.sampleService.lots || []).find(l => l.id === lotId);
    if (!lot) return;

    const lotRows: InlineLotRow[] = [this._buildLotRow(lot)];
    this._addLotsToTable(alloc, lotRows);
  }

  private _clearProductSearch() {
    this.productSearchText = '';
    this.productSearchResults = [];
    this.isSearchingProduct = false;
    this._destroyProductDropdown();
    const input = this.productSearchWrapEl?.nativeElement?.querySelector('input');
    if (input) input.blur();
  }

  private _removeCustomerAllocationProducts(): boolean {
    if (this.hasAllocationOrdersUnrestricted) return false;

    let removed = false;
    (this.allocation?.samples || [])
      .filter(sample => !sample.deleted)
      .forEach(sample => {
        sample.deleted = true;
        sample.totalQuantity = 0;
        if (sample.lots?.length) {
          sample.lots.forEach(lot => {
            lot.indskr_quantity = 0;
            lot.indskr_totalsamplevalue = 0;
            lot.deleted = true;
          });
        }
        removed = true;
      });

    if (this.productRows.length) {
      this.productRows = [];
      removed = true;
    }

    if (removed) {
      this._clearProductSearch();
      this.cd.markForCheck();
      this.saveSubject$.next();
    }
    return removed;
  }

  private _buildLotRow(lot: Lot): InlineLotRow {
    return {
      lotId: lot.id,
      lotName: lot.lotNumber || lot.name,
      expiryDisplay: this._formatLotExpiry(lot),
      lastDispatchDisplay: this._formatLotLastDispatch(lot),
      quantity: 0,
      unitPrice: lot.indskr_unitprice || 0,
      totalQuantityRemaining: lot.totalQuantityRemaining || 0,
      lotRefData: lot,
      activityLotRefData: null,
      isControlled: this._getSkuControlledState(lot.sampleSKUId, !!lot.isControlled),
    };
  }

  private _getSkuControlledState(skuId: string, fallback = false): boolean {
    const skuLots = (this.sampleService.lots || []).filter(l => l.sampleSKUId === skuId);
    return skuLots.some(l => !!l.isControlled) || fallback;
  }

  private _computeProductMeta(
    alloc: CustomerSampleAllocation | undefined,
    skuId: string,
    lots: InlineLotRow[],
  ): { dropLimitStr: string; valueRemainingStr: string; pricePerUnitStr: string; onHandQty: number } {
    let dropLimitStr = '';
    if (alloc && Array.isArray(this.sampleService.customerSampleProductEligibilities)) {
      try {
        const eligibility = this.sampleService.getCustomerSampleProductEligibilityForSku(
          skuId, alloc.id || '', alloc.allocationRulesProfileId || ''
        );
        if (eligibility) {
          dropLimitStr = `${eligibility.indskr_totalunitsdelivered || 0}/${eligibility.indskr_eligibilitylimit || 0}`;
        }
      } catch (e) { /* skip */ }
    }

    const valueRemainingNum = (alloc?.totalsamplevaluelimit ?? 0) > 0
      ? alloc.totalsamplevaluelimit - (alloc.indskr_totalsamplevaluedelivered || 0) : 0;
    const valueRemainingStr = (alloc?.totalsamplevaluelimit ?? 0) > 0
      ? (alloc?.currencySymbol || '') + this._formatNumber(valueRemainingNum) : '';

    const unitPrice = lots.length > 0 ? lots[0].unitPrice : 0;
    const pricePerUnitStr = unitPrice > 0
      ? (alloc?.currencySymbol || '') + this._formatNumber(unitPrice) : '';

    let onHandQty = 0;
    if (this.hasReceiptsTab && !this.isCompleted) {
      const skuLots = (this.sampleService.lots || []).filter(l =>
        l.sampleSKUId === skuId && l.status === 0 && this._checkValidLot(l));
      onHandQty = skuLots.reduce((sum, l) => sum + (l.totalQuantityRemaining || 0), 0);
    }

    return { dropLimitStr, valueRemainingStr, pricePerUnitStr, onHandQty };
  }

  /**
   * Adds lot rows to the product table. If the SKU already exists, merges into the existing row.
   * Otherwise creates a new product row.
   */
  private _addLotsToTable(alloc: CustomerSampleAllocation, lotRows: InlineLotRow[]) {
    const existingRow = this.productRows.find(r =>
      r.skuId === alloc.sampleSKUId && (r.allocId || '') === (alloc.id || '') && !r.isRemoving
    );

    let newRow: InlineProductRow | null = null;

    if (existingRow) {
      existingRow.lots = [...existingRow.lots, ...lotRows];
      existingRow.isControlled = this._getSkuControlledState(alloc.sampleSKUId, existingRow.lots.some(l => !!l.isControlled));
      this.productRows = [...this.productRows];
    } else {
      const meta = this._computeProductMeta(alloc, alloc.sampleSKUId, lotRows);
      // Resolve unlimited first, then gate orderLimit on it. An unlimited
      // alloc must NOT carry a per-visit cap — otherwise getMaxQuantityForLot
      // and _validateProductQuantity silently treat the row as capped even
      // though the UI labels it "Unlimited".
      const isUnlimited = !!alloc.isUnlimited;
      const productRow: InlineProductRow = {
        allocId: alloc.id || undefined,
        skuId: alloc.sampleSKUId,
        skuName: alloc.sampleSKUName,
        lots: lotRows,
        orderLimit: isUnlimited
          ? null
          : ((alloc.sampleDropLimitPerVisit || 0) > 0 ? alloc.sampleDropLimitPerVisit : null),
        isUnlimited,
        remaining: alloc.remaining || 0,
        currencySymbol: alloc.currencySymbol || '',
        totalsamplevaluelimit: alloc.totalsamplevaluelimit || 0,
        indskr_totalsamplevaluedelivered: alloc.indskr_totalsamplevaluedelivered || 0,
        allowvaluelimitunblock: alloc.allowvaluelimitunblock ?? true,
        valueRemaining: meta.valueRemainingStr,
        pricePerUnit: meta.pricePerUnitStr,
        customerDropLimit: meta.dropLimitStr,
        onHandQuantity: meta.onHandQty,
        eSignatureRequired: alloc.eSignatureNecessary || false,
        isValueLimitExceeded: false,
        isQuantityInvalid: false,
        isPending: true,
        isControlled: this._getSkuControlledState(alloc.sampleSKUId, lotRows.some(l => !!l.isControlled)),
      };
      newRow = productRow;
      this.productRows = [...this.productRows, productRow];
    }

    this._syncProductRowsToAllocation();
    this.cd.markForCheck();

    if (newRow) {
      this._persistNewRow(newRow);
    } else {
      this.saveSubject$.next();
    }
  }

  /**
   * Remove a single lot from a product row. If it's the last lot, the entire product row is removed.
   */
  removeLotRow(productIndex: number, lotIndex: number) {
    if (this.isLocked) return;
    if (this._persistInFlight) {
      this.notificationService.notify(this.translate.instant('PLEASE_WAIT_PREVIOUS_OPERATION'), '', 'top', ToastStyle.INFO);
      return;
    }
    const product = this.productRows[productIndex];
    if (!product || product.isPending) return;

    const removedLot = product.lots[lotIndex];
    const isLastLot = product.lots.length <= 1;
    const skuIsControlled = this._getSkuControlledState(product.skuId, !!product.isControlled);

    // Mark deleted on existing allocation.samples (matches NSA pattern:
    // mark deleted in-place on the existing entry, never push a new duplicate).
    // Do NOT call _syncProductRowsToAllocation() — allocation.samples already
    // has the correct deletion state for the payload.
    if (removedLot && this.allocation?.samples) {
      const existingSample = this.allocation.samples.find(s =>
        !s.deleted
        && s.indskr_skuid === product.skuId
        && (!product.allocId || !s.indskr_customersampleproductid || s.indskr_customersampleproductid === product.allocId)
      );
      if (existingSample) {
        if (isLastLot) {
          existingSample.deleted = true;
          existingSample.totalQuantity = 0;
          if (existingSample.lots?.length) {
            existingSample.lots.forEach(l => {
              l.indskr_quantity = 0;
              l.indskr_totalsamplevalue = 0;
              l.indskr_iscontrolled = skuIsControlled;
              l.deleted = true;
            });
          }
        } else {
          const existingLot = existingSample.lots?.find(l => l.indskr_lotid === removedLot.lotId);
          if (existingLot) {
            existingLot.indskr_quantity = 0;
            existingLot.indskr_totalsamplevalue = 0;
            existingLot.indskr_iscontrolled = skuIsControlled;
            existingLot.deleted = true;
          }
        }
      }
    }

    // Keep row in productRows (lots stay excluded from search) and show loader
    product.isPending = true;
    product.isRemoving = true;
    this.cd.markForCheck();

    this._persistRemoval(product, removedLot, isLastLot);
  }

  canIncrementLot(product: InlineProductRow, lot: InlineLotRow): boolean {
    return !this._validateProductQuantity(product, lot, lot.quantity + 1);
  }

  getMaxQuantityForLot(product: InlineProductRow, lot: InlineLotRow): number {
    const otherLotsQty = product.lots.reduce((s, l) => s + (l === lot ? 0 : l.quantity), 0);
    const limits: number[] = [];

    if (product.orderLimit) {
      limits.push(product.orderLimit - otherLotsQty);
    }
    // OMNI-62549: skip the on-hand cap for below-zero-eligible lots (AC-1); order/allocation limits stay.
    if (!product.isUnlimited && this.hasReceiptsTab && lot.totalQuantityRemaining >= 0 && !this.isLotIdBelowZeroEligible(lot.lotId)) {
      limits.push(lot.totalQuantityRemaining);
    }
    if (!product.isUnlimited && product.remaining > 0) {
      limits.push(product.remaining - otherLotsQty);
    }

    // Use the most restrictive limit, or 100 if no limits apply
    const max = limits.length ? Math.min(...limits) : 100;
    return Math.max(0, max);
  }

  getQuantityOptions(product: InlineProductRow, lot: InlineLotRow): number[] {
    const max = this.getMaxQuantityForLot(product, lot);
    const options: number[] = [];
    for (let i = 0; i <= max; i++) options.push(i);
    return options;
  }

  incrementLot(product: InlineProductRow, lot: InlineLotRow) {
    if (this.isLocked || product.isPending) return;
    if (!this.canIncrementLot(product, lot)) return;
    lot.quantity++;
    product.errorMessage = '';
    product.isQuantityInvalid = false;
    product.isValueLimitExceeded = false;
    this._syncProductRowsToAllocation();
    this._recalcProductMeta(product);
    this.cd.markForCheck();
    this.saveSubject$.next();
  }

  decrementLot(lot: InlineLotRow, product: InlineProductRow) {
    if (this.isLocked || product.isPending || lot.quantity <= 0) return;
    lot.quantity--;
    // Re-validate after decrement — quantity may still exceed limits
    const validationError = this._validateProductQuantity(product, lot, lot.quantity);
    if (validationError) {
      product.errorMessage = validationError;
      product.isQuantityInvalid = true;
    } else {
      product.errorMessage = '';
      product.isQuantityInvalid = false;
      product.isValueLimitExceeded = false;
    }
    this._syncProductRowsToAllocation();
    this._recalcProductMeta(product);
    this.cd.markForCheck();
    this.saveSubject$.next();
  }

  onLotQuantityInput(product: InlineProductRow, lot: InlineLotRow, event: Event) {
    if (product.isPending) return;
    const input = event.target as HTMLInputElement;
    let val = parseInt(input.value, 10);
    if (isNaN(val) || val < 0) val = 0;
    const validationError = this._validateProductQuantity(product, lot, val);
    if (validationError) {
      // Keep the typed value visible so user can correct it, but skip save
      lot.quantity = val;
      product.errorMessage = validationError;
      product.isQuantityInvalid = true;
      this.cd.markForCheck();
      return;
    }
    lot.quantity = val;
    product.errorMessage = '';
    product.isQuantityInvalid = false;
    product.isValueLimitExceeded = false;
    this._syncProductRowsToAllocation();
    this._recalcProductMeta(product);
    this.cd.markForCheck();
    this.saveSubject$.next();
  }

  /** Comprehensive validation matching new-sample-activity logic */
  private _validateProductQuantity(product: InlineProductRow, lot: InlineLotRow, newQty: number): string | null {
    // NOTE: SKU must match. The previous predicate (`a.id === product.allocId`)
    // returned true when both sides were undefined (unrestricted mode where
    // neither alloc.id nor product.allocId are set), causing .find() to pick
    // the FIRST eligible alloc for every row — limits then leaked across SKUs.
    const alloc = this.eligibleAllocations.find(a => {
      if (a.sampleSKUId !== product.skuId) return false;
      const have = a.id || '';
      const want = product.allocId || '';
      return !want || !have || have === want;
    });

    // 0. Currency mismatch check (allocCurrencyId vs skuCurrencyId)
    if (alloc && alloc.totalsamplevaluelimit > 0 && (alloc as any).allocCurrencyId && (alloc as any).skuCurrencyId
        && (alloc as any).allocCurrencyId !== (alloc as any).skuCurrencyId) {
      return this.translate.instant('SKU_CURRENCY_ALLOC_CURRENCY_ERROR_MSG') || 'Currency mismatch between allocation and SKU';
    }

    // 1. Order limit per visit
    const totalForProduct = product.lots.reduce((s, l) => s + (l === lot ? newQty : l.quantity), 0);
    if (product.orderLimit && totalForProduct > product.orderLimit) {
      return this.translate.instant('ALLOCATION_QUANTITY_EXCEEDS_ORDER_LIMIT') || 'Quantity exceeds order limit';
    }

    // 2. Lot remaining quantity check (only when on-hand inventory is tracked)
    // OMNI-62549: below-zero-eligible lots (in the last physical count) may be sampled past zero (AC-1);
    // the allocation/order limits below still apply (AC-11).
    if (!product.isUnlimited && this.hasReceiptsTab && !this.isLotIdBelowZeroEligible(lot.lotId)) {
      if (newQty > lot.totalQuantityRemaining) {
        return this.translate.instant('ALLOCATION_QUANTITY_EXCEEDS_ON_HAND') || 'Quantity exceeds available stock';
      }
    }

    // 3. Allocation remaining check (non-unlimited)
    if (!product.isUnlimited && product.remaining > 0) {
      if (totalForProduct > product.remaining) {
        return this.translate.instant('ALLOCATION_QUANTITY_EXCEEDS_ELIGIBLE_LIMIT') || 'Quantity exceeds allocation remaining';
      }
    }

    // 4. Value limit check (per-product)
    if (!product.allowvaluelimitunblock && product.totalsamplevaluelimit > 0 && lot.unitPrice > 0) {
      const totalSampleValue = product.lots.reduce((sum, l) => {
        const qty = l === lot ? newQty : l.quantity;
        return sum + (qty * (l.unitPrice || 0));
      }, 0);
      const valueRemaining = product.totalsamplevaluelimit - product.indskr_totalsamplevaluedelivered;
      if (totalSampleValue > valueRemaining) {
        product.isValueLimitExceeded = true;
        return this.translate.instant('SAMPLE_UNIT_PRICE_EXEEDS_LIMIT') || 'Value limit exceeded';
      }
    }

    // 4b. Cross-SKU value limit check (all products under same allocation)
    if (alloc && !product.allowvaluelimitunblock && product.totalsamplevaluelimit > 0) {
      let crossSkuTotalValue = 0;
      this.productRows.forEach(row => {
        if (row.allocId === product.allocId) {
          row.lots.forEach(l => {
            const qty = (row === product && l === lot) ? newQty : l.quantity;
            crossSkuTotalValue += qty * (l.unitPrice || 0);
          });
        }
      });
      const totalValueRemaining = product.totalsamplevaluelimit - product.indskr_totalsamplevaluedelivered;
      if (crossSkuTotalValue > totalValueRemaining) {
        product.isValueLimitExceeded = true;
        return this.translate.instant('SAMPLE_UNIT_PRICE_EXEEDS_LIMIT') || 'Cross-SKU value limit exceeded';
      }
    }

    // 5. Customer drop limit (BU product sampling)
    if (alloc?.allocationRulesProfileId && Array.isArray(this.sampleService.customerSampleProductEligibilities)) {
      try {
        const eligibility = this.sampleService.getCustomerSampleProductEligibilityForSku(
          product.skuId, alloc.id || '', alloc.allocationRulesProfileId
        );
        if (eligibility && eligibility.indskr_eligibilitylimit > 0) {
          if ((eligibility.indskr_totalunitsdelivered + totalForProduct) > eligibility.indskr_eligibilitylimit) {
            return this.translate.instant('CUSTOMER_DROP_LIMIT_EXEED_ERROR') || 'Customer drop limit exceeded';
          }
        }
      } catch (e) {
        // Eligibility data not ready yet — skip validation
      }
    }

    return null;
  }

  /** Recalculate dynamic display fields after quantity change */
  private _recalcProductMeta(product: InlineProductRow) {
    if (product.totalsamplevaluelimit > 0) {
      const totalSampleValue = product.lots.reduce((sum, l) => sum + (l.quantity * (l.unitPrice || 0)), 0);
      const valueRemaining = product.totalsamplevaluelimit - product.indskr_totalsamplevaluedelivered - totalSampleValue;
      product.valueRemaining = (product.currencySymbol || '') + this._formatNumber(Math.max(0, valueRemaining));
    }
  }

  getOrderLimitDisplay(product: InlineProductRow): string {
    if (product.isUnlimited) return this.translate.instant('UNLIMITED') || 'Unlimited';
    if (product.orderLimit) return `${product.orderLimit}`;
    return '—';
  }

  private _buildProductRows() {
    if (!this.allocation?.samples?.length) {
      this.productRows = [];
      this.cd.markForCheck();
      return;
    }
    const rows: InlineProductRow[] = [];
    const addedSkuIds = new Set<string>();
    this.allocation.samples
      .filter(s => !s.deleted)
      .forEach(sample => {
        // Deduplicate by SKU ID to prevent duplicate product rows
        const dedupeKey = (sample.indskr_customersampleproductid || '') + '_' + sample.indskr_skuid;
        if (addedSkuIds.has(dedupeKey)) return;
        addedSkuIds.add(dedupeKey);
        // Match by SKU first; only use alloc.id as a tie-breaker when both
        // sides are populated. Previous predicate matched "undefined===undefined"
        // in unrestricted mode and bound every sample to the first alloc,
        // leaking sampleDropLimitPerVisit / isUnlimited across products.
        let alloc = this.eligibleAllocations.find(a => {
          if (a.sampleSKUId !== sample.indskr_skuid) return false;
          const have = a.id || '';
          const want = sample.indskr_customersampleproductid || '';
          return !want || !have || have === want;
        });
        // Fallback: look up directly from service data if eligible allocations not loaded yet (e.g. after app relaunch)
        if (!alloc && sample.indskr_customersampleproductid) {
          const contactAllocs = this.sampleService.contactCustomerSampleAllocations?.find(
            a => a.contactId === this.allocation.contactID
          );
          alloc = contactAllocs?.currentCustomerSampleAllocations?.find(
            a => a.id === sample.indskr_customersampleproductid
          );
        }
        // Build lots from saved activity data, deduplicating by lot ID (matches new-sample-activity _getApplicableLotsForSKU)
        const seenLotIds = new Set<string>();
        const savedLots: InlineLotRow[] = (sample.lots || [])
          .filter(l => !l.deleted)
          // For completed AOs, only include lots with qty > 0 (matches new-sample-activity line 2000)
          .filter(l => !this.isCompleted || l.indskr_quantity > 0)
          // Deduplicate by lot ID — API can return duplicate lots for completed AOs
          .filter(l => {
            if (seenLotIds.has(l.indskr_lotid)) return false;
            seenLotIds.add(l.indskr_lotid);
            return true;
          })
          .map(l => {
            const lotRef = this.sampleService.lots?.find(ml => ml.id === l.indskr_lotid);
            const savedControlled = !!l.indskr_iscontrolled;
            return {
              lotId: l.indskr_lotid,
              lotName: l.indskr_lotname || l.indskr_lotnumber || '',
              expiryDisplay: this._formatActivityLotExpiry(l),
              lastDispatchDisplay: this._formatActivityLotLastDispatch(l),
              quantity: l.indskr_quantity || 0,
              unitPrice: l.indskr_unitprice || lotRef?.indskr_unitprice || 0,
              totalQuantityRemaining: lotRef?.totalQuantityRemaining || 0,
              lotRefData: lotRef || null,
              activityLotRefData: l,
              isControlled: this._getSkuControlledState(sample.indskr_skuid, savedControlled),
            };
          });

        // Promo drops: backend omits lot entries (indskr_lotid is empty, so the
        // lot join returns nothing). Synthesize the synthetic lot row so the product
        // remains interactive even when the server response has no lot data.
        if (this.isPromoMaterial && savedLots.length === 0) {
          savedLots.push({
            lotId: '',
            lotName: '',
            expiryDisplay: '',
            lastDispatchDisplay: '',
            quantity: sample.totalQuantity ?? sample.indskr_quantity ?? 0,
            unitPrice: 0,
            totalQuantityRemaining: 0,
            lotRefData: null,
            activityLotRefData: null,
          });
        }
        const lots: InlineLotRow[] = savedLots;
        const skuIsControlled = this._getSkuControlledState(sample.indskr_skuid, lots.some(l => !!l.isControlled));
        const meta = this._computeProductMeta(alloc, sample.indskr_skuid, lots);

        // Resolve unlimited first so we can gate orderLimit on it. Without
        // this guard, an unlimited SKU whose sample.samplelimitpervisit was
        // left set by an earlier (possibly incorrect) delta-sync enrichment
        // would render with a bogus cap. Unlimited → no cap, always.
        const isUnlimited = !!(alloc?.isUnlimited || (sample as any).indskr_isunlimited);
        const row: InlineProductRow = {
          allocId: sample.indskr_customersampleproductid || undefined,
          skuId: sample.indskr_skuid,
          skuName: sample.indskr_skuname,
          lots,
          // Prefer saved per-product limit (from fetchAllocationsMaxOrderQuantity) over generic allocation-level limit.
          // Unlimited rows always clear the limit so validation/quantity picker don't cap them.
          orderLimit: isUnlimited
            ? null
            : ((sample.samplelimitpervisit || alloc?.sampleDropLimitPerVisit || 0) > 0
                ? (sample.samplelimitpervisit || alloc?.sampleDropLimitPerVisit)
                : null),
          isUnlimited,
          remaining: alloc?.remaining || 0,
          currencySymbol: alloc?.currencySymbol || '',
          totalsamplevaluelimit: alloc?.totalsamplevaluelimit || 0,
          indskr_totalsamplevaluedelivered: alloc?.indskr_totalsamplevaluedelivered || 0,
          allowvaluelimitunblock: alloc?.allowvaluelimitunblock ?? true,
          valueRemaining: meta.valueRemainingStr,
          pricePerUnit: meta.pricePerUnitStr,
          customerDropLimit: meta.dropLimitStr,
          onHandQuantity: meta.onHandQty,
          eSignatureRequired: alloc?.eSignatureNecessary || sample.indskr_esignaturenecessaryflag || false,
          isValueLimitExceeded: false,
          isQuantityInvalid: false,
          errorMessage: '',
          isControlled: skuIsControlled,
        };
        // Re-validate saved quantities so errors persist across revisits
        if (!this.isCompleted && !this.isReadOnly) {
          for (const lot of row.lots) {
            if (lot.quantity > 0) {
              const err = this._validateProductQuantity(row, lot, lot.quantity);
              if (err) {
                row.errorMessage = err;
                row.isQuantityInvalid = true;
                break;
              }
            }
          }
        }
        rows.push(row);
      });
    // Assign new array reference for change detection
    this.productRows = rows;
    this.cd.markForCheck();
  }

  private _syncProductRowsToAllocation() {
    if (!this.allocation) return;
    // Collect deleted entries to preserve them (matching NSA: backend needs deleted flags)
    const deletedSamples = (this.allocation.samples || []).filter(s => s.deleted);

    const activeSamples = this.productRows.map(row => {
      const skuIsControlled = this._getSkuControlledState(row.skuId, !!row.isControlled);
      // Preserve deleted lots from the existing sample (e.g. single lot removal)
      const existingSample = (this.allocation.samples || []).find(s =>
        !s.deleted && s.indskr_skuid === row.skuId
        && (!row.allocId || s.indskr_customersampleproductid === row.allocId)
      );
      const deletedLots: LotDetailInSampleActivity[] = existingSample?.lots?.filter(l => l.deleted) || [];

      const activeLotDetails: LotDetailInSampleActivity[] = row.lots
        .map(l => {
          const validToStr = l.lotRefData?.validTo ? l.lotRefData.validTo.getTime().toString() : '';
          const validFromStr = l.lotRefData?.validFrom ? l.lotRefData.validFrom.getTime().toString() : '';
          return {
            indskr_quantity: l.quantity,
            indskr_lotid: l.lotId,
            indskr_lotname: l.lotName,
            indskr_lotvalidtodate: validToStr,
            indskr_lotvalidfromdate: validFromStr,
            indskr_lotnumber: l.lotName,
            formattedDate: l.lotRefData?.validTo || undefined,
            indskr_unitprice: l.unitPrice || 0,
            indskr_totalsamplevalue: l.unitPrice > 0 ? l.quantity * l.unitPrice : null,
            indskr_lastdispatchdate: l.lotRefData?.lastdispatchdate || '',
            indskr_iscontrolled: skuIsControlled,
            // OMNI-62549: let the backend permit this lot's on-hand to go below zero when it is
            // below-zero-eligible (in the last physical count).
            allowNegativeOnHand: this.isLotIdBelowZeroEligible(l.lotId),
            deleted: false,
          };
        });
      // Deduplicate: if a lot appears in both active and deleted (e.g. superseded removal), active wins
      const activeLotIds = new Set(activeLotDetails.map(l => l.indskr_lotid));
      const uniqueDeletedLots = deletedLots
        .filter(l => !activeLotIds.has(l.indskr_lotid))
        .map(l => ({ ...l, indskr_iscontrolled: skuIsControlled }));

      const totalQty = row.lots.reduce((s, l) => s + l.quantity, 0);
      const sample: SampleDetailInSampleActivity = {
        indskr_skuid: row.skuId,
        indskr_skuname: row.skuName,
        indskr_customersampleproductid: row.allocId || undefined,
        lots: [...activeLotDetails, ...uniqueDeletedLots],
        lotsCount: activeLotDetails.filter(l => l.indskr_quantity > 0).length,
        deleted: false,
        totalQuantity: totalQty,
        indskr_isunlimited: row.isUnlimited,
        indskr_esignaturenecessaryflag: row.eSignatureRequired,
        // Never persist a positive limit on an unlimited product — prevents a
        // stale limit from sticking across revisits once seeded incorrectly.
        samplelimitpervisit: row.isUnlimited ? 0 : (row.orderLimit || 0),
        currencySymbol: row.currencySymbol || '',
        // SKU-qualified match — see _buildProductRows for why the older
        // id-only predicate cross-contaminated rows in unrestricted mode.
        allocationRulesProfileId: this.eligibleAllocations.find(a =>
          a.sampleSKUId === row.skuId && ((a.id || '') === (row.allocId || '') || !a.id || !row.allocId)
        )?.allocationRulesProfileId || '',
      };
      return sample;
    });

    // Exclude deleted entries whose SKU already has an active entry (prevents IO_DupAllocDrop)
    const activeSkuKeys = new Set(activeSamples.map(s =>
      (s.indskr_customersampleproductid || '') + '_' + s.indskr_skuid
    ));
    const uniqueDeletedSamples = deletedSamples.filter(s =>
      !activeSkuKeys.has((s.indskr_customersampleproductid || '') + '_' + s.indskr_skuid)
    );
    this.allocation.samples = [...activeSamples, ...uniqueDeletedSamples];
  }

  private _formatDate(date: Date | string | number | null | undefined): string {
    if (date == null) return '';
    try {
      return this.datePipe.transform(date, this.dateTimeFormatsService.date, undefined, this.translate.currentLang) || '';
    } catch { return ''; }
  }

  private _formatLotExpiry(lot: Lot): string {
    return (!lot.validTo || !isValid(new Date(lot.validTo))) ? '' : this._formatDate(lot.validTo);
  }

  private _formatActivityLotExpiry(lot: LotDetailInSampleActivity): string {
    if (!lot.indskr_lotvalidtodate) return '';
    return this._formatDate(Utility.changeUTCDateToLocalDateWith0Time(parseInt(lot.indskr_lotvalidtodate), true));
  }

  private _formatLotLastDispatch(lot: Lot): string {
    return lot.lastdispatchdate ? this._formatDate(new Date(lot.lastdispatchdate)) : '';
  }

  private _formatActivityLotLastDispatch(lot: LotDetailInSampleActivity): string {
    return lot.indskr_lastdispatchdate ? this._formatDate(new Date(lot.indskr_lastdispatchdate)) : '';
  }

  private _formatNumber(value: number): string {
    if (value == null || isNaN(value)) return '0';
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  }

  /** Get valid lots for a SKU to show in the product search dropdown */
  private _getLotsForDropdown(alloc: CustomerSampleAllocation): Lot[] {
    return (this.sampleService.lots || [])
      .filter(l =>
        l.sampleSKUId === alloc.sampleSKUId
        && l.status === 0
        && this._checkValidLot(l)
        // OMNI-62549: include below-zero lots from the last physical count (AC-1).
        && (!this.hasReceiptsTab || l.totalQuantityRemaining > 0 || this.isLotBelowZeroEligible(l))
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  /** Build "Last Dispatch Date X │ Expires Y" label for a lot (matches new-sample-activity) */
  private _getDateInfoLabelForLot(lot: Lot): string {
    let lastDispatchStr = '';
    let expiresStr = '';

    if (lot.lastdispatchdate && lot.lastdispatchdate !== 'Invalid Date') {
      lastDispatchStr = this.translate.instant('AL_LAST_DISPATCH_DATE') + ' ' + this._formatLotLastDispatch(lot);
    } else if (this.sampleLastDispatchLimitNum > 0 && lot.validTo) {
      const calculated = subDays(new Date(lot.validTo), this.sampleLastDispatchLimitNum);
      if (calculated && isValid(calculated)) {
        try {
          const formatted = this.datePipe.transform(calculated, this.dateTimeFormatsService.date, undefined, this.translate.currentLang) || '';
          if (formatted) lastDispatchStr = this.translate.instant('AL_LAST_DISPATCH_DATE') + ' ' + formatted;
        } catch { /* ignore */ }
      }
    }

    if (lot.validTo && isValid(new Date(lot.validTo))) {
      expiresStr = this.translate.instant('AL_EXPIRES') + ' ' + this._formatLotExpiry(lot);
    }

    if (lastDispatchStr && expiresStr) return `${lastDispatchStr} │ ${expiresStr}`;
    return lastDispatchStr || expiresStr || '';
  }

  // ─── Terms ────────────────────────────────────────────────────────────────

  private _loadConsentTerms() {
    this.consentService.allConsentTermsAllocationOrdersSubject.pipe(
      takeUntil(this.ngDestroy$),
    ).subscribe(terms => {
      if (!terms) return;
      const allocTerms = terms.filter(t => t.indskr_allocationordersubmission);
      allocTerms.sort((a, b) =>
        new Date(b['modifiedon']).getTime() - new Date(a['modifiedon']).getTime()
      );
      this.consentTerm = allocTerms[0] || this._defaultConsentTerm();
      this._processTermsBody();
    });
  }

  private _defaultConsentTerm(): ConsentTerm {
    return {
      _id: '', _rev: '', indskr_consenttermsid: '', indskr_consentid: '',
      indskr_consentTermName: 'Default',
      indskr_fromdate: Date.now(), indskr_untildate: Date.now(),
      createdon: Date.now(), modifiedon: Date.now(),
      indskr_alwaysrequired: true, indskr_recaptureconsent: true,
      indskr_body: '',
      indskr_contactid: '', offlineId: '', indskr_signature: '',
      indskr_positionid: '', userid: '',
      indskr_signaturerequired: 548910001,
      products: [] as any, channels: [] as any,
      track_action: 11,
      indskr_confirmationmessage: this.translate.instant('ONCE_ORDER_CONF_NO_MODIFIED'),
      indskr_agreementmessage: this.translate.instant('I_AGREE_ON_TERMS'),
      indskr_allocationordersubmission: true,
    } as ConsentTerm;
  }

  private _processTermsBody() {
    if (!this.consentTerm?.indskr_body) { this.termsBody = null; this.termsOverflows = false; return; }
    try {
      this.rawTermsBody = String(this.consentTerm.indskr_body);
      this.termsBody = this.sanitizer.bypassSecurityTrustHtml(this.rawTermsBody);
      // Wait for DOM to render the terms body element, then check overflow
      setTimeout(() => this._checkTermsOverflow(), 200);
    } catch { this.termsBody = null; this.termsOverflows = false; }
  }

  private _checkTermsOverflow() {
    const el = this.termsBodyEl?.nativeElement;
    if (!el) {
      // Element not in DOM yet — retry once after another tick
      setTimeout(() => {
        const retryEl = this.termsBodyEl?.nativeElement;
        if (!retryEl) { this.termsOverflows = false; return; }
        this._measureOverflow(retryEl);
      }, 300);
      return;
    }
    this._measureOverflow(el);
  }

  private _measureOverflow(el: HTMLDivElement) {
    // Temporarily remove max-height to measure natural content height
    const origMaxHeight = el.style.maxHeight;
    el.style.maxHeight = 'none';
    const naturalHeight = el.scrollHeight;
    el.style.maxHeight = origMaxHeight;
    // 50px is the collapsed max-height defined in CSS
    this.termsOverflows = naturalHeight > 50;
    this.cd.markForCheck();
  }

  toggleTermsExpand() {
    this.termsViewLess = !this.termsViewLess;
  }

  // ─── Can Complete / Signature ─────────────────────────────────────────────

  get canSign(): boolean {
    if (this.isLocked) return false;
    if (!this.allocation?.contactID) return false;
    // Require both the displayed address string AND the persisted addressID. The
    // string can be populated from allocation.location while the ID never resolved
    // (e.g. addresses not synced), so gating on the string alone let address-less
    // orders reach completion. The ID is what the server persists.
    if (!this.selectedAddress || !this.allocation.addressID) return false;
    if (this._stateLicensePillsApplicable()) {
      if (this.addressLicenseChecking) return false;
      if (this.addressLicenseValid !== true) return false;
    } else if (this.addressLicenseValid === false) {
      return false;
    }
    if (this.isOhioTDDDFlow) {
      if (this.addressLicenseChecking) return false;
      const result = this.addressTDDD;
      if (!result || (!result.hasCat2 && !result.hasCat3)) return false;
      if (this.hasControlledProduct && !result.hasCat3) return false;
    }
    if (!this.productRows.length) return false;
    // Every product must have at least one lot, and every lot must have qty > 0
    const allLotsHaveQty = this.productRows.every(r => r.lots.length > 0 && r.lots.every(l => l.quantity > 0));
    if (!allLotsHaveQty) return false;
    if (this.productRows.some(r => r.isQuantityInvalid || r.errorMessage || r.isPending)) return false;
    return true;
  }

  get hasControlledProduct(): boolean {
    return this.productRows.some(r =>
      this._getSkuControlledState(r.skuId, !!r.isControlled || (r.lots || []).some(l => !!l.isControlled))
    );
  }

  /** When false, inline meeting AO may complete with Submit (no e-sign) per format IO or BU (OMNI-59159). */
  get isInlineSampleDropSignatureRequired(): boolean {
    if (this.isPromoMaterial) return false;
    const buEsign = !!this.authService.hasIoConfigWithFeatureCode(
      IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_ESIGNATURENECESSARY
    );
    const meetingId = this.meetingId;
    if (!meetingId) {
      return buEsign;
    }
    const meeting = this.activityService.getActivityByID(meetingId) as AppointmentActivity;
    const formatId = meeting?.indskr_activitytype;
    if (formatId) {
      const sampleRule = this.meetingMandatoryConfig
        .getMandatoryRulesForFormat(formatId)
        .find(r => r.activityType === 'SampleDrop');
      if (sampleRule && sampleRule.requireSignature !== undefined) {
        return sampleRule.requireSignature;
      }
    }
    return buEsign;
  }

  get tdddBlockingMessage(): string {
    if (!this.isOhioTDDDFlow || this.addressLicenseChecking) return '';
    const result = this.addressTDDD;
    if (!result) return '';
    if (this.hasControlledProduct && !result.hasCat3) return this.translate.instant('TDDD_CAT3_REQUIRED');
    return '';
  }

  async submitSampleDropWithoutSignature(): Promise<void> {
    if (this.isInlineSampleDropSignatureRequired) {
      return;
    }
    if (!this.canSign) {
      return;
    }
    if (!this.isPromoMaterial && this.selectedAddressObj?.stateId) {
      const licenseStillValid = await this.sampleDataService.hasValidStateLicense(
        this.selectedAddressObj.stateId,
        this.allocation.contactID,
        this.device.isOffline ? { loadFromDbOnly: true } : {}
      );
      if (!licenseStillValid) {
        this.addressLicenseValid = false;
        this.addressErrorMessage = this.translate.instant('LICENSE_INVALID');
        this.cd.detectChanges();
        return;
      }
    }
    this._syncProductRowsToAllocation();
    await this._completeAllocation('');
  }

  async initiateSignature() {
    if (!this.canSign) return;

    // Re-validate license before proceeding (matches new-sample-activity).
    // State license is gating; TDDD only runs when state is valid AND Ohio.
    if (this.selectedAddressObj?.stateId) {
      const stateId = this.selectedAddressObj.stateId;
      const contactId = this.allocation.contactID;
      const addressId = this.selectedAddressObj.addressId || '';

      const licenseStillValid = await this.sampleDataService.hasValidStateLicense(stateId, contactId, { loadFromDbOnly: true });
      this.addressStateLicenseValid = licenseStillValid;
      if (!licenseStillValid) {
        this.addressLicenseValid = false;
        this.cd.markForCheck();
        return;
      }

      if (this._isTDDDApplicable(this.selectedAddressObj)) {
        const categories = await this.sampleDataService.getValidTDDDCategoriesForLocation(addressId);
        const hasCat2 = categories.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat2);
        const hasCat3 = categories.has(CUSTOMER_LICENSE_TYPE.TDDD_Cat3);
        this.addressTDDD = { hasCat2, hasCat3 };
        if (!hasCat2 && !hasCat3) {
          this.addressLicenseValid = false;
          this.cd.markForCheck();
          return;
        }
        if (this.hasControlledProduct && !hasCat3) {
          this.notificationService.notify(this.translate.instant('TDDD_CAT3_REQUIRED'), 'sample-service', 'top', ToastStyle.DANGER);
          this.cd.markForCheck();
          return;
        }
      }
    }

    this._syncProductRowsToAllocation();
    this._navigateToCaptureAllocationOrder((signatureData) => this._completeAllocation(signatureData || ''));
  }

  viewCompletedSignature() {
    this._navigateToCaptureAllocationOrder();
  }

  private _navigateToCaptureAllocationOrder(completionHandler?: (signatureData: string) => Promise<void>) {
    // Ensure allocation.samples is in sync with productRows before handing to capture component.
    this._syncProductRowsToAllocation();
    const prevViewMode = this.sampleService.samplingDetailsViewMode;
    this.sampleService.samplingDetailsViewMode = SamplingDetailsViewMode.CREATE_FROM_MEETING_PRESENTATION;
    this.sampleService.inMeetingAllocationActivity = this.allocation;

    const orderCompletionEventHandler = completionHandler
      ? (signatureData: string) => {
          this.sampleService.samplingDetailsViewMode = prevViewMode;
          this.sampleService.inMeetingAllocationActivity = undefined;
          completionHandler(signatureData);
        }
      : undefined;

    this.navService.pushWithPageTracking(
      CaptureAllocationOrderComponent,
      PageName.CaptureAllocationOrderComponent,
      { orderCompletionEventHandler, fromInlineAllocation: true },
    );
  }

  private async _completeAllocation(signatureData: string) {
    if (this.isSaving) return;
    // Wait for any in-flight auto-save to finish so the completion call
    // always lands after intermediate saves — prevents server-side state overwrite.
    if (this._persistInFlight) {
      await this._persistInFlight;
    }
    this.isSaving = true;
    this._buildContactFormField();
    this.cd.detectChanges(); // ensure overlay renders before async API call
    try {
      const now = new Date().getTime().toString();
      const payload = new CreateSampleDropRequestBody(this.allocation);
      payload.indskr_signature = signatureData;
      payload.statecode = 1;
      payload.statuscode = 2;
      payload.actualend = now;
      payload.indskr_appointmentid = this.meetingId || this.allocation.appointmentID;

      this.allocation.state = 1;
      this.allocation.status = 2;
      this.allocation.statusString = 'Completed';
      this.allocation.actualEnd = now;
      this.allocation.signature = signatureData;

      const contact = this.contactService.getContactByID(this.allocation.contactID);
      await this.sampleDataService.updateSampleActivity(payload, this.allocation, contact);

      this.activityService.addNewActivityToSampleActivityMetasIndexedBySKU(this.allocation);
      this.sampleService.updateSampleEligibilities(this.allocation, null);
      if (this.authService.hasFeatureAction(FeatureActionsMap.RECIEPTS_TAB)) {
        this.sampleService._addToLotDroppedQuantityDetail(this.allocation);
      }

      this.capturedSignature = signatureData;
      this.isCompleted = true;
      this.isTermsAccepted = true;
      this.notificationService.notify(
        this.translate.instant(this.isPromoMaterial ? 'PROMO_MATERIAL_SUB_SUCCESSFULLY' : 'ALLOCATION_O_SUB_SUCCESSFULLY'),
        '',
        'top',
        ToastStyle.INFO,
      );
      this.onCompleted.emit(this.allocation);
    } catch (e) {
      console.error('Complete allocation failed', e);
      // Roll back state so UI reflects the failure (matches new-sample-activity error recovery)
      this.allocation.state = 0;
      this.allocation.status = 1;
      this.allocation.statusString = '';
      this.isCompleted = false;
      this.notificationService.notify(
        this.translate.instant('ALLOCATION_COMPLETE_FAILED') || 'Failed to complete order',
        '',
        'top',
        ToastStyle.DANGER,
      );
    } finally {
      this.isSaving = false;
      this._buildContactFormField();
      this.cd.detectChanges();
    }
  }

  // ─── Scrap ────────────────────────────────────────────────────────────────

  async scrapAllocation() {
    if (this.isSaving || this.isLocked) return;
    const isPromo = this.allocation.isPromoMaterial;
    const confirmed = await this.alertService.showAlert(
      {
        title: this.translate.instant(isPromo ? 'SCRAP_PROMOTIONAL_MATERIAL' : 'SCRAP_ALLOCATION_ORDER'),
        message: this.translate.instant(isPromo ? 'CONFIRM_SCRAP_PROMOTIONAL_MATERIAL' : 'CONFIRM_SCRAP_ALLOCATION'),
      },
      this.translate.instant('SCRAP'),
    );
    if (confirmed?.role !== 'ok') return;

    // Wait for any in-flight auto-save before scrapping
    if (this._persistInFlight) {
      await this._persistInFlight;
    }
    this.isSaving = true;
    this._buildContactFormField();
    this.cd.detectChanges(); // ensure overlay renders before async API call
    try {
      const payload = new CreateSampleDropRequestBody(this.allocation);
      payload.statecode = 2;
      payload.statuscode = 3;
      this.allocation.state = 2;
      this.allocation.status = 3;
      await this.sampleDataService.updateSampleActivity(payload, this.allocation);
      this.isScrapped = true;
      this.onScrapped.emit(this.allocation);
    } catch (e) {
      console.error('Scrap allocation failed', e);
    } finally {
      this.isSaving = false;
      this._buildContactFormField();
      this.cd.detectChanges();
    }
  }

  // ─── Real-time Online Sync ──────────────────────────────────────────────

  private async _onlineSyncOnInit() {
    if (this.device.isOffline || this.device.isUserStateOffline || this.activityService.hasOfflineSampleOrderData(this.allocation.offlineActivityId)) return;
    const allocationIdAtStart = this.allocation?.ID;
    try {
      const tasks: Promise<any>[] = [];
      const hasServerId = !!this.allocation?.ID && !this.allocation.ID.includes('offline_');

      let maxOrderResults: Array<{ sampleProductId: string, maxOrderQuantity: number }> | null = null;

      if (hasServerId) {
        tasks.push(
          this.sampleDataService.fetchSampleDropActivityOnline(this.allocation.ID)
            .then((raw: any) => {
              if (this._isDestroyed) return;
              if (this.allocation?.ID !== allocationIdAtStart) return;
              this._applyRefreshedSampleDrop(raw);
            })
            .catch(e => console.warn('IAO fetchSampleDropActivityOnline failed', e)),
        );
      }

      if (!this.isLocked) {
        const dataRange = this.authService.getFromToDateRangeInUTCMiliSec(undefined);
        tasks.push(
          this.sampleDataService.syncLots(dataRange)
            .catch(e => console.warn('IAO syncLots failed', e)),
        );
        // Skip fetchInventory if already in memory (matches new-sample-activity)
        if (!this.allocationInventoryService.userInventory?.length) {
          tasks.push(
            this.allocationInventoryDataService.fetchInventory(true, false)
              .catch(e => console.warn('IAO fetchInventory failed', e)),
          );
        }
        if (this.hasAllocationOrdersUnrestricted) {
          tasks.push(
            this.sampleDataService.fetchAllocationsMaxOrderQuantity(undefined, false)
              .then((results: Array<{ sampleProductId: string, maxOrderQuantity: number }> | undefined) => {
                if (Array.isArray(results)) maxOrderResults = results;
              })
              .catch(e => console.warn('IAO fetchAllocationsMaxOrderQuantity failed', e)),
          );
        }
      }
      await Promise.all(tasks);

      if (this._isDestroyed) return;
      if (this.allocation?.ID !== allocationIdAtStart) return;

      if (maxOrderResults) {
        this._maxOrderQtyMap.clear();
        maxOrderResults.forEach(r => this._maxOrderQtyMap.set(r.sampleProductId, r.maxOrderQuantity));
      }

      // Update lot lastdispatchdate from inventory (matches new-sample-activity.updateLatestUserLotInfo)
      if (!this.isLocked && this.allocationInventoryService.userInventory?.length && this.sampleService.lots?.length) {
        this.sampleService.lots.forEach(lot => {
          if (lot.sampleSKUId) {
            lot.lastdispatchdate = this.allocationInventoryService.getLastDispatchDateString(lot.sampleSKUId, lot.id);
          }
        });
      }

      if (!this.isLocked) {
        this._loadEligibleAllocations();
        this._buildProductRows();
      }
      this.cd.detectChanges();
    } catch (e) {
      console.error('Online sync on init failed, falling back to local data', e);
    }
  }

  private _applyRefreshedSampleDrop(raw: any | null) {
    if (!raw || !this.allocation || this._isDestroyed) return;
    const respId = raw['activityid'] || raw['activityId'];
    if (respId !== this.allocation.ID) return;

    if (this.isLocked) {
      const refreshed = new SampleActivity(raw);
      this.allocation.state = refreshed.state;
      this.allocation.status = refreshed.status;
      if (refreshed.signature && this.allocation.signature !== refreshed.signature) {
        this.allocation.signature = refreshed.signature;
        this.capturedSignature = refreshed.signature;
      }
      const localHasSamples = Array.isArray(this.allocation.samples) && this.allocation.samples.length > 0;
      const serverHasSamples = Array.isArray(refreshed.samples) && refreshed.samples.length > 0;
      if (!localHasSamples && serverHasSamples) {
        this.allocation.samples = refreshed.samples;
        this._buildProductRows();
      }
      this.cd.markForCheck();
      return;
    }

    // Carry forward indskr_allocationordertype from the GET response so isPromoMaterial
    // stays correct even when the local PouchDB record was created before this field was
    // propagated correctly (e.g. when POST response omitted it and the record was cached).
    if (raw['indskr_allocationordertype'] && !this.allocation.indskr_allocationordertype) {
      this.allocation.indskr_allocationordertype = raw['indskr_allocationordertype'];
    }

    if (Array.isArray(raw['activitySampleDrops']) && Array.isArray(this.allocation.samples)) {
      raw['activitySampleDrops'].forEach((refSample: any) => {
        const skuId = refSample?.indskr_skuid;
        if (!skuId) return;
        const localSample = this.allocation.samples.find(s => s.indskr_skuid === skuId);
        if (localSample && refSample.samplelimitpervisit != null) {
          localSample.samplelimitpervisit = refSample.samplelimitpervisit;
        }
        if (refSample.samplelimitpervisit != null) {
          this._maxOrderQtyMap.set(skuId, refSample.samplelimitpervisit);
        }
      });
    }
  }



  // ─── Persist ──────────────────────────────────────────────────────────────

  /** Serialized persist runner — waits for any in-flight save, then executes the callback.
   *  Callers must prepare allocation.samples before calling (either via _syncProductRowsToAllocation
   *  for additions, or by marking deleted flags directly for removals). */
  private async _runPersist(fn: () => Promise<void>): Promise<void> {
    if (this._persistInFlight) await this._persistInFlight;
    const run = async () => {
      try {
        await fn();
      } finally {
        this._persistInFlight = null;
      }
    };
    this._persistInFlight = run();
    await this._persistInFlight;
    this.cd.markForCheck();
  }

  private _buildPayload(): CreateSampleDropRequestBody {
    this._ensureAddressBeforePersist();
    const payload = new CreateSampleDropRequestBody(this.allocation);
    payload.indskr_appointmentid = this.meetingId || this.allocation.appointmentID;
    return payload;
  }

  /**
   * Preventive guard against uploading an allocation order with a missing address.
   * Auto-select only picks from addresses already in the local DB, so when a
   * contact's addresses haven't synced yet (offline / freshly added contact) a
   * draft could be persisted with a blank addressID/location. If the contact now
   * has addresses available but none is applied, repair the selection here — at
   * the single choke point every draft/auto-save write passes through — before the
   * payload is built. Completion and scrap build their payloads separately and are
   * unaffected (completion is already address-gated by canSign).
   */
  private _ensureAddressBeforePersist(): void {
    if (this.isLocked) return;
    if (!this.allocation?.contactID) return;      // location-only order: nothing to pick
    if (this.allocation.addressID) return;         // already resolved
    if (!this.contactAddressList?.length) return;  // no addresses available yet — can't repair here
    const autoPick = this._findAddressMatchingMeetingLocation()
      || this.contactAddressList.find(a => a.isPrimary)
      || this.contactAddressList[0];
    if (!autoPick) return;
    this._applyAddress(autoPick, null);
    this.cd.markForCheck();
    // Keep the license pill honest for the newly applied address (fire-and-forget).
    this._validateLicenseInBackground(autoPick);
  }

  private async _persistAllocation() {
    if (!this.allocation || this.isCompleted || this.isScrapped) return;
    this._syncProductRowsToAllocation();
    await this._runPersist(async () => {
      try {
        await this.sampleDataService.updateSampleActivity(this._buildPayload(), this.allocation);
        this.onSaved.emit(this.allocation);
      } catch (e) {
        console.error('Auto-save failed', e);
      }
    });
  }

  private async _persistNewRow(product: InlineProductRow) {
    this._syncProductRowsToAllocation();
    await this._runPersist(async () => {
      try {
        await this.sampleDataService.updateSampleActivity(this._buildPayload(), this.allocation);
        product.isPending = false;
        this.onSaved.emit(this.allocation);
      } catch (e) {
        console.error('Failed to persist new product row', e);
        this.productRows = this.productRows.filter(r => r !== product);
        this._syncProductRowsToAllocation();
        this.notificationService.notify(
          this.translate.instant('PRODUCT_ADD_FAILED') || 'Failed to add product. Please try again.',
          '', 'top', ToastStyle.DANGER,
        );
      }
    });
  }

  private async _persistRemoval(product: InlineProductRow, removedLot: InlineLotRow, isLastLot: boolean) {
    await this._runPersist(async () => {
      try {
        await this.sampleDataService.updateSampleActivity(this._buildPayload(), this.allocation);
        product.isRemoving = false;
        if (isLastLot) {
          this.productRows = this.productRows.filter(r => r !== product);
        } else {
          product.lots = product.lots.filter(l => l !== removedLot);
          product.isControlled = this._getSkuControlledState(product.skuId, product.lots.some(l => !!l.isControlled));
          product.isPending = false;
          this.productRows = [...this.productRows];
        }
        this._syncProductRowsToAllocation();
        this.onSaved.emit(this.allocation);
      } catch (e) {
        console.error('Failed to persist product removal', e);
        this._revertDeletionMarks(product, removedLot, isLastLot);
        product.isPending = false;
        product.isRemoving = false;
        this._syncProductRowsToAllocation();
        this.notificationService.notify(
          this.translate.instant('PRODUCT_REMOVE_FAILED') || 'Failed to remove product. Please try again.',
          '', 'top', ToastStyle.DANGER,
        );
      }
    });
  }

  /** Undo deletion marks on allocation.samples so _syncProductRowsToAllocation can rebuild cleanly. */
  private _revertDeletionMarks(product: InlineProductRow, removedLot: InlineLotRow, isLastLot: boolean) {
    if (!this.allocation?.samples) return;
    if (isLastLot) {
      const sample = this.allocation.samples.find(s =>
        s.deleted && s.indskr_skuid === product.skuId
        && (!product.allocId || s.indskr_customersampleproductid === product.allocId)
      );
      if (sample) {
        sample.deleted = false;
        sample.totalQuantity = product.lots.reduce((sum, l) => sum + l.quantity, 0);
        sample.lots?.forEach(l => {
          const match = product.lots.find(pl => pl.lotId === l.indskr_lotid);
          if (match) {
            l.deleted = false;
            l.indskr_quantity = match.quantity;
            l.indskr_totalsamplevalue = match.unitPrice > 0 ? match.quantity * match.unitPrice : null;
          }
        });
      }
    } else {
      const sample = this.allocation.samples.find(s =>
        !s.deleted && s.indskr_skuid === product.skuId
        && (!product.allocId || s.indskr_customersampleproductid === product.allocId)
      );
      const lotEntry = sample?.lots?.find(l => l.indskr_lotid === removedLot.lotId && l.deleted);
      if (lotEntry) {
        lotEntry.deleted = false;
        lotEntry.indskr_quantity = removedLot.quantity;
        lotEntry.indskr_totalsamplevalue = removedLot.unitPrice > 0
          ? removedLot.quantity * removedLot.unitPrice : null;
      }
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  get showSignatureArea(): boolean {
    return !!this.selectedAddress && (this.isCompleted || this.productRows.some(r => r.lots.some(l => l.quantity > 0)));
  }

  get hasTermsBody(): boolean {
    return !!this.rawTermsBody;
  }

  trackByProductRow(_index: number, row: InlineProductRow): string {
    return row.allocId + '_' + row.skuId;
  }

  trackByLotRow(_index: number, lot: InlineLotRow): string {
    return lot.lotId;
  }
}
